// o2p.js — move an object along a path
// -----------------------------------------------------------
// Modes:
//   - forward
//   - reverse
//   - alternate (true ping-pong)
// Features:
//   - dur (beats of tempo())
//   - tdelay (beats): the wait before it starts
//   - loop (0 = infinite)
//   - rotate: 0..4 (rotation modes)
//   - rotspeed, rotdir, rotoffset, rotlock
//   - osc:false, 0, true, or number (throttle ms)
//   - ease: single int/string or list [...]
//   - start/end: 0..1 segment slicing (start:auto = where the object sits)
//   - uid override
//   - armed three-phase lifecycle
// -----------------------------------------------------------
// Geometry is never shifted: the original bbox center is stored and
// transforms are computed relative to it. This preserves the object's
// position in SVG coordinate space.
// -----------------------------------------------------------

import { isOff, truthy } from "../parser/cueUtils.js";
import { registerAnimation, autoStartAnimation } from "./animation.js";
import { isObjectTrig } from "./probeTriggers.js";
import { scheduleCueStart } from "./cueDispatcher.js";
import { runNext } from "./cueNext.js";
import { isLeader, seekAnimation } from "./animSync.js";
import { mayOperate } from "./controlAccess.js";
import { set as busSet, get as busGet } from "../control/paramBus.js";

// <uid>.end on the parameter bus: 0 when a run starts, 1 when a finite run
// (loop:1, loop:3 …) completes. A map-mode station can wait for it —
// station(until:"<uid>.end") — and so can any trig: condition.
function noteEnd(uid, v) {
    if (uid == null) return;
    try { busSet(`${uid}.end`, v, { source: "o2p" }); } catch { /* no bus */ }
}
import { createHitLabel, destroyHitLabel, repositionAllHitLabels, repositionHitLabelFast, updateHitLabelValue, updateHitCircleColor, loadOscState, loadToggleState } from "./animOverlays.js";
import { initO2PDragHandler, createRotationRing, initO2PRotationDragHandler,
         enableO2PPauseDrag, disableO2PPauseDrag, findClosestTOnPathFast,
         getPointOnPath, getPathLength, parseSimpleLine } from "../control/o2pTouch.js";
import {
    applyPrestateBeforeStart,
    applyPrestateOnStart,
    armElement,
    installOscToggleHandler,
    installToggleStatesHandler
} from "./animShared.js";

import { createOscOverlay } from "./osc.js";
import { sendOSC } from "../system/oscillaOSCClient.js";
import { svgLocalMatrix } from "../system/worldCoords.js";

import { publish, velocityChannels } from '../control/paramBinding.js';
import { setupSpeedBinding, setupParamBinding, isSignalRef, registerFaderUpdateCallback } from './cueParamBinding.js';
import { createAnimationLoop, createAlternatingLoop } from './cueAnimLoop.js';
import { getEasing } from '../control/easing.js';
import { compilePass } from './o2pSchedule.js';
import { polygonVertexTs, checkVertexCrossing, invertEase } from './animMath.js';
import { secs, secPerBeat } from './beats.js';

import {
    mapToSpatial, buildOSCArgs, formatSpatialOverlay, resolveBounds
} from '../control/spatialMap.js';

import {
    normalizeHmodeValue, parseRotRange,
    constrainRotation, normalizeRotation, denormalizeRotation
} from '../control/rotationMath.js';


/* ---------------------------------------------------------
 *  Helper: Apply OSC toggle state from sync data
 * --------------------------------------------------------*/
function applyOscSyncState(cfg, oscEnabled, oscColor) {
    if (!cfg.oscToggle || oscEnabled === undefined) return;

    cfg._oscEnabled = oscEnabled;
    if (cfg.oscCfg) cfg.oscCfg.enabled = oscEnabled;

    const hitRec = window._oscillaHitLabels?.find(r => r.uid === cfg.uid);
    if (hitRec && (hitRec.oscMode !== oscEnabled || oscColor)) {
        hitRec.oscMode = oscEnabled;
        if (oscColor) hitRec.oscColor = oscColor;
        updateHitCircleColor(hitRec);
    }
}


/* ---------------------------------------------------------
 *  Compute sweep bounds for world coordinate normalization
 *  Walks up DOM to find rotating ancestor, computes center + max radius
 *  Returns { cx, cy, radius } in SVG coordinates or null if no rotation
 * --------------------------------------------------------*/
function computeSweepBounds(pathEl, startEl) {
    if (!pathEl || !startEl) return null;

    // Walk up DOM to find rotating ancestor with _oscillaCfg
    let rotatingAncestor = null;
    let current = startEl.parentElement;

    while (current && current !== document.documentElement) {
        const cfg = current._oscillaCfg;
        // Check if this element has a rotation animation (CSS transform with rotate)
        const hasRotateStyle = current.style?.transform?.includes('rotate');
        // rotate.js tags its cfg with kind, not type
        const hasRotateCfg = cfg && (cfg.kind === 'rotate' || cfg.type === 'rotate' || cfg._rotating);

        if (hasRotateStyle || hasRotateCfg) {
            rotatingAncestor = current;
            break;
        }
        current = current.parentElement;
    }

    if (!rotatingAncestor) {
        return null;
    }

    // Get rotation center from rotating ancestor's bounding box
    const bb = rotatingAncestor.getBBox?.();
    if (!bb || (bb.width === 0 && bb.height === 0)) return null;

    // Calculate center (same logic as rotate.js applySvgPivot)
    const bboxCx = bb.x + bb.width / 2;
    const bboxCy = bb.y + bb.height / 2;

    // For elements centered at origin, use exact (0,0)
    const tolerance = 50;
    const cx = (Math.abs(bboxCx) < tolerance && Math.abs(bboxCy) < tolerance) ? 0 : bboxCx;
    const cy = (Math.abs(bboxCx) < tolerance && Math.abs(bboxCy) < tolerance) ? 0 : bboxCy;

    // Sample points along the path to find max distance from rotation center
    const pathLength = pathEl.getTotalLength?.();
    if (!pathLength) return null;

    let maxDist = 0;
    const samples = 32; // Sample 32 points along path

    for (let i = 0; i <= samples; i++) {
        const t = i / samples;
        const pt = pathEl.getPointAtLength(t * pathLength);
        const dx = pt.x - cx;
        const dy = pt.y - cy;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist > maxDist) maxDist = dist;
    }

    if (maxDist === 0) return null;

    // Transform center into the SVG's user space — the same space
    // getWorldPosition reports in, and the one maxDist was measured in
    const svg = rotatingAncestor.ownerSVGElement;
    let worldCx = cx, worldCy = cy;
    if (svg) {
        try {
            const pt = svg.createSVGPoint();
            pt.x = cx;
            pt.y = cy;
            const ctm = svgLocalMatrix(rotatingAncestor);
            if (ctm) {
                const worldPt = pt.matrixTransform(ctm);
                worldCx = worldPt.x;
                worldCy = worldPt.y;
            }
        } catch (e) {
            console.warn('[o2p] Failed to transform sweep center:', e);
        }
    }

    return { cx: worldCx, cy: worldCy, radius: maxDist };
}

/* ---------------------------------------------------------
 *  Compute world position of element after all transforms
 *  Returns { x, y } in SVG root coordinates
 * --------------------------------------------------------*/
function getWorldPosition(wrapper) {
    if (!wrapper) return null;

    const svg = wrapper.ownerSVGElement;
    if (!svg) return null;

    try {
        // The wrapper's transform is translate(point - originalCenter), so
        // it is the ORIGINAL CENTER that lands on the path point. (0,0) would
        // give the displacement from the start, ~0 for any dot that begins
        // at its path's start.
        const oc = wrapper._o2pOriginalCenter || { x: 0, y: 0 };
        const pt = svg.createSVGPoint();
        pt.x = oc.x;
        pt.y = oc.y;

        // Into the SVG's user space, with the viewBox / zoom scale stripped
        // (a bare getCTM() carries it, so worldX/Y would change with window size)
        const ctm = svgLocalMatrix(wrapper);
        if (!ctm) return null;

        const worldPt = pt.matrixTransform(ctm);
        return { x: worldPt.x, y: worldPt.y };
    } catch (e) {
        console.warn('[o2p] getWorldPosition error:', e);
        return null;
    }
}

/* ---------------------------------------------------------
 *  Rotation indicator visual update helpers
 * --------------------------------------------------------*/

/**
 * Update the auto-generated rotation ring indicator dot position
 * @param {Object} ringData - from createRotationRing()
 * @param {number} angleDeg - angle in the 7-o'clock-zero coordinate system
 */
function updateRotationIndicator(ringData, angleDeg) {
    if (!ringData || !ringData.dot) return;
    // Convert from 7-o'clock-zero to standard SVG angle (add 120)
    const standardAngle = angleDeg + 120;
    const angleRad = (standardAngle * Math.PI) / 180;
    const r = ringData.radius;
    ringData.dot.setAttribute("cx", Math.cos(angleRad) * r);
    ringData.dot.setAttribute("cy", Math.sin(angleRad) * r);
}

/**
 * Update a user-supplied rotation handle element position
 * Translates the element to follow the fader's current path position
 * and rotates it to show current rotation angle
 * @param {Object} faderEntry - fader registry entry
 * @param {number} angleDeg - angle in the 7-o'clock-zero coordinate system
 */
function updateO2PUserHandle(faderEntry, angleDeg) {
    if (!faderEntry || !faderEntry.userHandleEl) return;
    const el = faderEntry.userHandleEl;
    const point = faderEntry.cfg._currentPoint;
    if (!point) return;

    // Get the element's original center
    const bbox = el.getBBox();
    const cx = bbox.x + bbox.width / 2;
    const cy = bbox.y + bbox.height / 2;

    // Translate to fader position and rotate
    const standardAngle = angleDeg + 120;
    const dx = point.x - cx;
    const dy = point.y - cy;
    el.setAttribute("transform",
        `translate(${dx}, ${dy}) rotate(${standardAngle} ${cx} ${cy})`);
}


/* ---------------------------------------------------------
 *  1. ensureO2PWrapper(el)
 *     Creates a dedicated wrapper for o2p transforms.
 *     This wrapper sits INSIDE the element and receives
 *     all o2p translation/rotation. The outer element
 *     retains its scroll-mode placement transform.
 * --------------------------------------------------------*/
function ensureO2PWrapper(el) {
    // If we already have a wrapper, return it
    if (el._o2pAnimWrapper) {
        return el._o2pAnimWrapper;
    }

    // A leaf (circle / rect / path) can hold only ONE transform, and o2p uses
    // it for positioning — but rotate/scale need one too, and two modules
    // cannot share one attribute (and CSS transform would override the SVG
    // one). So give the leaf a positioning GROUP and animate that: o2p becomes
    // the outer frame and an in-place wrapper (ensureAnimWrapper) nests between
    // the group and the leaf. Wrap OUTSIDE any spin wrapper the leaf already
    // sits in, so the order the cues were written does not decide the geometry.
    if (!(el instanceof SVGGElement)) {
        // Position the leaf, or the in-place wrapper already around it.
        //
        // Climb ONLY into a wrapper that belongs to this element alone. An
        // oscilla-anim holding more than one child belongs to an ancestor group
        // — a rotate() or scale() on a <g> that contains this leaf and its
        // siblings — and swallowing it would put those siblings inside the
        // positioning group too. For an o2p that means the PATH travels along
        // with the object it is supposed to be guiding: the pair stays glued
        // together and the motion vanishes, because the dot never moves
        // relative to its own path.
        //
        // Ownership cannot be read from el._oscillaAnimWrapper, because
        // ensureAnimWrapper adopts an existing parent wrapper rather than
        // creating its own — so the child count is the honest test.
        let mount = el;
        const _near = mount.parentNode;
        if (_near?.classList?.contains?.("oscilla-anim") && _near.childElementCount === 1) {
            mount = _near;
        }
        const parent = mount.parentNode;
        if (!parent) { el._o2pAnimWrapper = el; return el; }   // detached — fall back
        let wrapper = parent.classList?.contains?.("oscilla-o2p-anim") ? parent : null;
        if (!wrapper) {
            wrapper = document.createElementNS("http://www.w3.org/2000/svg", "g");
            wrapper.classList.add("oscilla-o2p-anim");
            parent.insertBefore(wrapper, mount);
            wrapper.appendChild(mount);
        }
        el._o2pAnimWrapper = wrapper;
        return wrapper;
    }

    // Check if wrapper already exists in DOM
    const existing = el.querySelector(":scope > g.oscilla-o2p-anim");
    if (existing) {
        el._o2pAnimWrapper = existing;
        return existing;
    }

    // Create new wrapper
    const wrapper = document.createElementNS("http://www.w3.org/2000/svg", "g");
    wrapper.classList.add("oscilla-o2p-anim");

    // Move all children into the wrapper
    while (el.firstChild) {
        wrapper.appendChild(el.firstChild);
    }

    // Attach wrapper to element
    el.appendChild(wrapper);
    el._o2pAnimWrapper = wrapper;

    return wrapper;
}


/* ---------------------------------------------------------
 *  2. captureOriginalCenter(wrapper)
 *     
 *     Captures the bbox center of the wrapper content
 *     WITHOUT modifying any geometry. This center is used
 *     to compute the offset needed when positioning on path.
 *     
 *     KEY INSIGHT: We don't move the geometry to (0,0).
 *     Instead, we remember where it was and compute
 *     transforms that move it FROM its original position
 *     TO the path position.
 * --------------------------------------------------------*/
function captureOriginalCenter(wrapper, cfg) {
    // Guard: don't re-capture
    if (wrapper._o2pCenterCaptured) {
        return wrapper._o2pOriginalCenter;
    }

    // anchor: — the point of the object that rides the path, in its own
    // coordinates: origin is its (0,0), [x, y] any point. Unlike the bbox
    // centre it does not move when the drawing grows, so a generated score
    // can place the path exactly.
    const a = cfg?.anchor;
    if (a === "origin" || Array.isArray(a)) {
        wrapper._o2pOriginalCenter = a === "origin" ? { x: 0, y: 0 } : { x: Number(a[0]) || 0, y: Number(a[1]) || 0 };
        wrapper._o2pCenterCaptured = true;
        return wrapper._o2pOriginalCenter;
    }

    // Compute bbox in local coordinates
    const bbox = wrapper.getBBox();

    // Guard against an invalid or sizeless bbox. A straight line has no
    // height (or no width) but still a centre.
    if (!bbox || !isFinite(bbox.x) || !isFinite(bbox.y) ||
        !isFinite(bbox.width) || !isFinite(bbox.height) ||
        (bbox.width === 0 && bbox.height === 0)) {
        console.warn("[o2p] captureOriginalCenter: invalid or zero-area bbox for", wrapper);
        wrapper._o2pOriginalCenter = { x: 0, y: 0 };
        wrapper._o2pCenterCaptured = true;
        return wrapper._o2pOriginalCenter;
    }

    const cx = bbox.x + bbox.width / 2;
    const cy = bbox.y + bbox.height / 2;

    // Store original center - this is where the object currently sits
    wrapper._o2pOriginalCenter = { x: cx, y: cy };
    wrapper._o2pCenterCaptured = true;

    return wrapper._o2pOriginalCenter;
}


/* ---------------------------------------------------------
 *  3. ease controller
 * --------------------------------------------------------*/
function normalizeEase(easeVal) {
    const easingMap = {
        0: "linear",
        1: "easeInSine",
        2: "easeOutSine",
        3: "easeInOutSine",
        4: "easeInBack",
        5: "easeOutBack",
        6: "easeInOutBack",
        7: "easeInElastic",
        8: "easeOutElastic",
        9: "easeInOutElastic"
    };

    let seq = [];

    // An object is the parser's funcCall node — ease:in(1.05) — which getEasing
    // resolves itself; String() would silently turn it into easeInOutSine.
    const one = e => typeof e === "number" ? (easingMap[e] || "easeInOutSine")
        : (e && typeof e === "object") ? e
        : String(e);

    if (Array.isArray(easeVal)) {
        seq = easeVal.map(one);
    } else if (easeVal == null) {
        seq = ["easeInOutSine"];
    } else {
        seq = [one(easeVal)];
    }

    // One easing per traversal of the path; a list cycles (next() on each one).
    const fns = seq.map(name => getEasing(name));
    let i = 0;
    return {
        fn: fns[0],
        next() { this.fn = fns[++i % fns.length]; }
    };
}


/* ---------------------------------------------------------
 *  4. VirtualPath - Unified sampler with segment slicing
 *     (Optimized: caches individual path lengths)
 * --------------------------------------------------------*/
class VirtualPath {
    constructor(paths) {
        this.paths = paths;
        this.pathLengths = [];  // Cached lengths for each path
        if (!paths || paths.length === 0) {
            this.totalLen = 0;
            this.closed = false;
            return;
        }
        this.totalLen = this.computeTotalLength();
        // Detect closed path: check if SVG path data ends with Z/z
        // or if start and end points are very close
        this.closed = this._detectClosed();
    }

    _detectClosed() {
        if (!this.paths || this.paths.length === 0) return false;
        const last = this.paths[this.paths.length - 1];
        const d = last.getAttribute("d") || "";
        if (/[Zz]\s*$/.test(d.trim())) return true;
        // Fallback: check if endpoints match (use cached length)
        const len = this.pathLengths[this.pathLengths.length - 1] || 0;
        if (len <= 0) return false;
        const p0 = last.getPointAtLength(0);
        const p1 = last.getPointAtLength(len);
        const dist = Math.hypot(p1.x - p0.x, p1.y - p0.y);
        return dist < 1; // within 1px
    }

    computeTotalLength() {
        let sum = 0;
        this.pathLengths = [];
        for (const p of this.paths) {
            const len = p.getTotalLength();
            this.pathLengths.push(len);
            sum += len;
        }
        return sum;
    }

    sample(globalT) {
        if (!this.totalLen) return null;

        if (!isFinite(globalT)) return null;
        globalT = Math.max(0, Math.min(1, globalT));

        let target = globalT * this.totalLen;
        let acc = 0;

        for (let i = 0; i < this.paths.length; i++) {
            const path = this.paths[i];
            const L = this.pathLengths[i];  // Use cached length
            if (acc + L >= target) {
                const local = target - acc;
                if (!path.isConnected) return null;
                const point = path.getPointAtLength(local);
                const pathT = L > 0 ? (local / L) : 0;
                return { path, point, pathT, distance: local };
            }
            acc += L;
        }
        return null;
    }
}


/* ---------------------------------------------------------
 *  4b. start:auto — begin where the object sits in the SVG
 *
 *  Resolved once per element and cached: the first fire strips the
 *  element's transform and moves it onto the path, so a later read
 *  would see the moved position, not the authored one. Before the
 *  cfg exists (first parse) startVal returns the "auto" marker and
 *  handleO2PCue resolves it; on hot updates the cached value is used.
 * --------------------------------------------------------*/
function startVal(cfg, v) {
    if (!/^\s*auto\s*$/i.test(String(v))) return Number(v);
    return cfg?._autoStartT ?? "auto";
}

/**
 * The element o2p follows. path: may name the geometry itself or a group
 * around it: a made shape -- shape(sides:1), a line -- is <g id> around its
 * <path>, and getTotalLength() exists only on the geometry.
 */
/**
 * The matrices from a path's space and from an element's to a common one.
 * getCTM() stops at the nearest <svg>: a path inside a nested one (a block
 * use() put on the stage) and an object outside it are measured in two
 * different spaces, so then both go to the screen instead.
 */
function sharedCTMs(pathEl, el) {
    const root = n => n.ownerSVGElement ?? n;
    if (root(pathEl) === root(el)) return [pathEl.getCTM(), el.getCTM()];
    return [pathEl.getScreenCTM(), el.getScreenCTM()];
}

export function pathElOf(id, root = null) {
    if (!id) return null;
    const el = root?.getElementById?.(id) ?? document.getElementById(id);
    if (!el || typeof el.getTotalLength === "function") return el;
    return el.querySelector?.("path, line, polyline, polygon, circle, ellipse, rect") ?? el;
}

function resolveAutoStart(el, pathId) {
    if (el._o2pAutoStartT != null) return el._o2pAutoStartT;
    let t = 0;
    try {
        const svg = el.ownerSVGElement;
        const pathEl = pathElOf(pathId, svg);
        const bb = el.getBBox();
        const [pathCTM, elCTM] = pathEl ? sharedCTMs(pathEl, el) : [null, null];
        if (pathEl && elCTM && pathCTM) {
            const pt = svg.createSVGPoint();
            pt.x = bb.x + bb.width / 2;
            pt.y = bb.y + bb.height / 2;
            // element-local → viewport → path-local (the space getPointAtLength works in)
            const p = pt.matrixTransform(elCTM).matrixTransform(pathCTM.inverse());
            t = findClosestTOnPathFast(pathEl, p.x, p.y);
            const on = getPointOnPath(pathEl, t);
            // ponytail: tolerance is the untransformed bbox diagonal; fine unless the object is heavily scaled by an ancestor
            if (Math.hypot(on.x - p.x, on.y - p.y) > Math.hypot(bb.width, bb.height)) {
                console.warn(`[o2p] start:auto — object is not on path "${pathId}", snapping to nearest point (t=${t.toFixed(3)})`);
            }
        } else {
            console.warn(`[o2p] start:auto — cannot resolve position for path "${pathId}", using 0`);
        }
    } catch (err) {
        console.warn("[o2p] start:auto failed, using 0:", err);
    }
    el._o2pAutoStartT = t;
    return t;
}

/* ---------------------------------------------------------
 *  5. Path segment mapper (start/end)
 * --------------------------------------------------------*/
function makeTMapper(start, end) {
    const a = Math.max(0, Math.min(1, start ?? 0));
    const b = Math.max(0, Math.min(1, end ?? 1));
    const d = b - a;
    return t => a + t * d;
}

/* ---------------------------------------------------------
 *  5b. Mode alias normalisation
 * --------------------------------------------------------*/
const MODE_ALIASES = { fwd: "forward", rev: "reverse", alt: "alternate" };
function normalizeMode(v) {
    const s = String(v).toLowerCase();
    return MODE_ALIASES[s] || s;
}

/* ---------------------------------------------------------
 *  5c. Hot-updatable param keys
 *      Used by live console edit: compare then merge.
 * --------------------------------------------------------*/
// A list-valued dur/at compares by its text: Number([8,4]) is NaN, so the diff
// below would call every scan a change and thrash _needsRestart.
const listy = v => JSON.stringify(v ?? null);

// dur: arrives in beats and the cfg holds seconds: set and norm each turn
// the incoming value once (it is never stored back in beats)
const HOT_PARAMS = {
    dur:      { get: c => c.durList ? listy(c.durList) : c.dur,
                set: (c,v) => { v = secs(v);
                                if (Array.isArray(v)) { c.durList = v.map(d => Number(d) > 0 ? Number(d) : 1); c.dur = c.durList[0]; }
                                else { c.durList = null; c.dur = Number(v) || c.dur; } },
                norm: v => { v = secs(v); return Array.isArray(v) ? listy(v.map(d => Number(d) > 0 ? Number(d) : 1)) : (Number(v) || 1); } },
    at:       { get: c => listy(c.at), set: (c,v) => c.at = Array.isArray(v) ? v.map(Number) : null,
                norm: v => listy(Array.isArray(v) ? v.map(Number) : null) },
    loop:     { get: c => c.loop,     set: (c,v) => c.loop = Number(v),         norm: v => Number(v) },
    mode:     { get: c => c.mode,     set: (c,v) => c.mode = normalizeMode(v),  norm: v => normalizeMode(v) },
    // listy, not String(): String() compares a list lossily, and two different
    // parameterised eases both stringify to "[object Object]", so a live edit
    // of in(1.05) to in(3) would look like no change at all.
    ease:     { get: c => listy(c.ease), set: (c,v) => c.ease = v,                norm: v => listy(v) },
    start:    { get: c => c.startPos, set: (c,v) => c.startPos = startVal(c,v), norm: (v,c) => startVal(c,v) },
    startPos: { get: c => c.startPos, set: (c,v) => c.startPos = startVal(c,v), norm: (v,c) => startVal(c,v) },
    end:      { get: c => c.endPos,   set: (c,v) => c.endPos = Number(v),       norm: v => Number(v) },
    endPos:   { get: c => c.endPos,   set: (c,v) => c.endPos = Number(v),       norm: v => Number(v) },
    osc:      { get: c => String(c.osc), set: (c,v) => c.osc = v,              norm: v => String(v) },
    oscaddr:  { get: c => c.oscAddr,  set: (c,v) => c.oscAddr = String(v),      norm: v => String(v) },
    oscAddr:  { get: c => c.oscAddr,  set: (c,v) => c.oscAddr = String(v),      norm: v => String(v) },
};

function diffAndMergeParams(cfg, args) {
    let changed = false;
    for (const a of args) {
        const hp = HOT_PARAMS[a.type];
        if (!hp) continue;
        if (hp.get(cfg) !== hp.norm(a.value, cfg)) {
            hp.set(cfg, a.value);
            changed = true;
        }
    }
    return changed;
}


/* ---------------------------------------------------------
 *  6. TRANSFORM WRITER
 *     
 *     Computes the transform needed to move the object
 *     FROM its original center TO the path point.
 *     
 *     Transform = translate(pathPoint - originalCenter) + rotate
 *     
 *     This means:
 *     - Object at (40000, 500) with center at (40050, 520)
 *     - Path point at (40100, 530)
 *     - Translation = (40100-40050, 530-520) = (50, 10)
 *     - Object moves 50px right and 10px down from its original spot
 * --------------------------------------------------------*/
// rotate: by name → its numeric code (applyTransform, every frame)
const ROTATE_MODES = { none: 0, aligned: 1, locked: 3, spin: 4 };

function applyTransform(wrapper, point, angleDeg, cfg, pathEl) {
    const now = performance.now();

    // Map the path-local point into the wrapper's parent space (differs when
    // the path has its own transform or lives in another group).
    // ponytail: angle is not remapped; needed only for rotated paths
    const parent = wrapper.parentNode;
    if (pathEl && parent?.getCTM) {
        const [pctm, wctm] = sharedCTMs(pathEl, parent);
        if (pctm && wctm) {
            const pt = wrapper.ownerSVGElement.createSVGPoint();
            pt.x = point.x; pt.y = point.y;
            point = pt.matrixTransform(pctm).matrixTransform(wctm.inverse());
        }
    }

    // Resolve cfg.rotate to numeric mode
    let mode = cfg.rotate;

    if (typeof mode === "string") {
        const key = mode.toLowerCase().trim();
        if (ROTATE_MODES[key] !== undefined) {
            mode = ROTATE_MODES[key];
        } else {
            mode = 0;
        }
    }

    if (!Number.isFinite(mode)) {
        mode = 0;
    }

    // Get the original center (where the object was before any o2p transform)
    const originalCenter = wrapper._o2pOriginalCenter || { x: 0, y: 0 };

    // Compute translation: move FROM original center TO path point
    const tx = point.x - originalCenter.x;
    const ty = point.y - originalCenter.y;

    // Build transform string
    // First translate to move center to path point
    // Then rotate around the new center position
    let t = `translate(${tx}, ${ty})`;

    if (!isFinite(angleDeg)) angleDeg = 0;

    // For rotation, we need to rotate around the center
    // Since we've translated, the center is now at the path point
    // We rotate around (0,0) relative to the translated position
    // which means rotating around originalCenter in local coords

    const rotateAroundX = originalCenter.x;
    const rotateAroundY = originalCenter.y;

    switch (mode) {
        case 0:
            // no rotation
            break;

        case 1:
            // tangent-aligned - rotate around the object's center
            t += ` rotate(${angleDeg}, ${rotateAroundX}, ${rotateAroundY})`;
            break;

        case 2:
            // tangent + static offset
            t += ` rotate(${angleDeg + (cfg.rotoffset || 0)}, ${rotateAroundX}, ${rotateAroundY})`;
            break;

        case 3:
            // locked heading
            t += ` rotate(${cfg.rotlock || 0}, ${rotateAroundX}, ${rotateAroundY})`;
            break;

        case 4:
            // free spin
            if (!wrapper._o2pFreeSpin) wrapper._o2pFreeSpin = 0;
            {
                let dt;
                if (wrapper._o2pLastSpinTime == null) {
                    dt = window._o2p_dt || 0.016;
                } else {
                    dt = (now - wrapper._o2pLastSpinTime) / 1000;
                    if (dt > 0.05) dt = 0.05;
                }
                wrapper._o2pLastSpinTime = now;

                // rotspeed controls degrees per second directly
                // Curve (exp2, exp3, etc.) is applied by the binding system via DSL
                // e.g., rotspeed:fader.t-0-240-0-exp2 gives quadratic response
                // Negative rotspeed = reverse spin (bipolar fader, centre = stopped)
                const degPerSecond = cfg.rotspeed ?? 0;
                const dir = cfg.rotdir || 1;

                wrapper._o2pFreeSpin += degPerSecond * dir * dt;
                t += ` rotate(${wrapper._o2pFreeSpin}, ${rotateAroundX}, ${rotateAroundY})`;
            }
            break;
    }

    // Apply transform to the wrapper ONLY via SVG attribute
    wrapper.setAttribute("transform", t);

    // DO NOT touch style.transform - that's for CSS animations (nested rotate)
}


/* ---------------------------------------------------------
 *  Spatial output (spatial:plan | spatial:dome)
 *  Where the source is on the bounds circle, as azimuth /
 *  elevation / distance (spatialMap.js has the conventions).
 *  Source and circle are both measured in the SVG's user
 *  space, so groups that rotate or scale either one are
 *  allowed for.
 * --------------------------------------------------------*/

/** A circle from `el`'s own coordinates into the SVG's user space. */
function toWorldCircle(el, c) {
    const m = svgLocalMatrix(el);
    if (!m) return c;
    return {
        cx: m.a * c.cx + m.c * c.cy + m.e,
        cy: m.b * c.cx + m.d * c.cy + m.f,
        radius: c.radius * Math.sqrt(Math.abs(m.a * m.d - m.b * m.c))
    };
}

/**
 * The circle a spatial o2p maps onto, in user space: the element named by
 * bounds: (a circle, an ellipse's smaller radius, or any shape's box), else
 * the circle the path sweeps in a rotating group, else the path's own box.
 * Also stores the same circle in the wrapper's parent space as
 * cfg._spatialBounds, which pause-drag clamps to.
 */
function spatialCircle(cfg, pathEl) {
    if (cfg._spatialWorld) return cfg._spatialWorld;
    let world = null;
    const boundsEl = cfg.bounds ? document.getElementById(cfg.bounds) : null;
    if (cfg.bounds && !boundsEl) console.warn(`[o2p] ${cfg.uid}: bounds:${cfg.bounds} -- no element with that id`);
    if (boundsEl) world = toWorldCircle(boundsEl, resolveBounds(boundsEl));
    else if (cfg._sweepBounds) world = cfg._sweepBounds;
    else if (pathEl) {
        const b = pathEl.getBBox();
        world = toWorldCircle(pathEl, { cx: b.x + b.width / 2, cy: b.y + b.height / 2, radius: Math.max(b.width, b.height) / 2 });
    }
    if (!world || !(world.radius > 0)) return null;
    cfg._spatialWorld = world;

    // the same circle in the space pause-drag moves the wrapper in
    const parent = cfg._wrapper?.parentNode;
    const pm = parent?.ownerSVGElement ? svgLocalMatrix(parent) : null;
    if (pm) {
        const inv = pm.inverse();
        cfg._spatialBounds = {
            cx: inv.a * world.cx + inv.c * world.cy + inv.e,
            cy: inv.b * world.cx + inv.d * world.cy + inv.f,
            radius: world.radius * Math.sqrt(Math.abs(inv.a * inv.d - inv.b * inv.c))
        };
    } else cfg._spatialBounds = world;
    return world;
}

/**
 * The source's spatial position from its world position: published on the
 * bus as <uid>.azi / .elev / .dist (0..1, for follow()), sent as OSC in the
 * chosen format (every argument a float) when osc is on. Returns it.
 */
function emitSpatial(cfg, worldPos, pathEl) {
    const c = spatialCircle(cfg, pathEl);
    if (!c || !worldPos) return null;
    // plan mode takes its elevation from elev: (degrees, 0 = ear level)
    const elev01 = Math.max(0, Math.min(1, (Number(cfg.elev) || 0) / 180 + 0.5));
    const sp = mapToSpatial(cfg.spatial, worldPos.x - c.cx, worldPos.y - c.cy, c.radius, elev01);

    publish("spatial", cfg.uid, { azi: sp.aziNorm, elev: sp.elevNorm, dist: sp.distNorm });

    if (cfg.oscCfg?.enabled) {
        sendOSC({
            type: "osc_value",
            addr: cfg.oscAddr || `spat/${cfg.uid || "unknown"}`,
            args: buildOSCArgs(sp, cfg.spatialFormat || "aed"),
            float: true,
            timestamp: Date.now()
        });
    }
    cfg._overlay?.update?.(formatSpatialOverlay(sp, cfg.spatialFormat || "aed"));
    return sp;
}

/* ---------------------------------------------------------
 *  7. OSC emitter
 * --------------------------------------------------------*/
const O2P_OSC_THROTTLE_MS = 30;


function emitO2POsc({ cfg, uid, path, point, pathT, rotationP = 0 }) {

    const oscCfg = cfg.oscCfg;

    // Vertex crossing detection MUST run on every tick (before throttle checks)
    // Otherwise we miss crossings when OSC values are being throttled
    if (cfg._vertices && cfg._vertices.length > 0) {
        const vertexIndex = checkVertexCrossing(cfg._lastT, pathT, cfg._vertices);

        if (vertexIndex >= 0) {
            // Determine address for vertex event
            let vertexAddr;
            if (cfg.oscAddr) {
                vertexAddr = cfg.oscAddr + "/vertex";
            } else {
                vertexAddr = `o2p/${cfg.uid || "unknown"}/vertex`;
            }

            sendOSC({
                type: "osc_value",
                addr: vertexAddr,
                args: [vertexIndex],
                timestamp: Date.now()
            }, { immediate: true }); // Use immediate to bypass throttle for events
        }

        cfg._lastT = pathT;
    }

    // spatial: the position in space instead of the 9 floats; on the bus
    // whether or not OSC is on
    if (cfg.spatial) {
        const t = performance.now();
        if (t - (cfg._spatialLastSent || 0) < Math.max(O2P_OSC_THROTTLE_MS, oscCfg?.throttle || 0)) return;
        cfg._spatialLastSent = t;
        emitSpatial(cfg, cfg._wrapper ? getWorldPosition(cfg._wrapper) : point, path);
        return;
    }

    // Early exit if OSC not enabled - but vertex detection above still runs
    if (!oscCfg || !oscCfg.enabled) return;

    const now = performance.now();
    if (oscCfg.lastSent && (now - oscCfg.lastSent < oscCfg.throttle)) return;
    oscCfg.lastSent = now;

    if (!cfg._oscLastSent) cfg._oscLastSent = 0;
    if (now - cfg._oscLastSent < O2P_OSC_THROTTLE_MS) return;
    cfg._oscLastSent = now;

    // Cache bbox on path element (static path)
    const bbox = path._cachedBBox ?? (path._cachedBBox = path.getBBox());
    // Allow zero-width (vertical) or zero-height (horizontal) paths
    if (!bbox || (bbox.width === 0 && bbox.height === 0)) return;

    // For straight lines, use pathT directly; avoid division by zero
    const normX = bbox.width > 0 ? (point.x - bbox.x) / bbox.width : 0.5;
    const normY = bbox.height > 0 ? (point.y - bbox.y) / bbox.height : pathT;

    // Compute tangent angle - use simple line detection for straight paths
    const line = parseSimpleLine(path);
    let angle;
    if (line) {
        angle = Math.atan2(line.y2 - line.y1, line.x2 - line.x1) * (180 / Math.PI);
    } else {
        // Cache length on path element
        const length = path._cachedLen ?? (path._cachedLen = path.getTotalLength());
        const EPS = 0.1;
        const localL = pathT * length;
        const aheadLen = Math.min(length - EPS, Math.max(0, localL + EPS));
        const ahead = path.getPointAtLength(aheadLen);
        angle = Math.atan2(ahead.y - point.y, ahead.x - point.x) * (180 / Math.PI);
    }

    angle = (angle + 360) % 360;

    if (!Number.isFinite(angle)) angle = 0;

    let addr;

    if (cfg.oscAddr) {
        addr = cfg.oscAddr;   // leading slash = full address, sent as written
    } else {
        addr = `o2p/${cfg.uid || "unknown"}`;
    }

    // For faders (touch mode), just use pathT directly - no normalization needed
    let tLocal = pathT;

    // Compute world position (after all transforms including parent rotation)
    let worldX = point.x;
    let worldY = point.y;
    let sweepNormX = 0.5;
    let sweepNormY = 0.5;

    const wrapper = cfg._wrapper;
    if (wrapper) {
        const worldPos = getWorldPosition(wrapper);

        if (worldPos) {
            worldX = worldPos.x;
            worldY = worldPos.y;

            // Normalize to sweep bounds if available
            const sb = cfg._sweepBounds;
            if (sb && sb.radius > 0) {
                // Map from circle to 0-1 square (center of circle = 0.5, 0.5)
                sweepNormX = (worldX - (sb.cx - sb.radius)) / (sb.radius * 2);
                sweepNormY = (worldY - (sb.cy - sb.radius)) / (sb.radius * 2);

                // Clamp to 0-1 range
                sweepNormX = Math.max(0, Math.min(1, sweepNormX));
                sweepNormY = Math.max(0, Math.min(1, sweepNormY));
            }
        }
    }

    sendOSC({
        type: "osc_value",
        addr,
        args: [
            tLocal,        // 0: path position (0-1)
            normX,         // 1: path-relative X
            normY,         // 2: path-relative Y
            angle,         // 3: tangent angle
            worldX,        // 4: world position X
            worldY,        // 5: world position Y
            sweepNormX,    // 6: sweep-normalized X (0-1 in rotating parent bounds)
            sweepNormY,    // 7: sweep-normalized Y
            rotationP      // 8: fader rotation/p value (0-1)
        ],
        timestamp: Date.now()
    });
}

/* ---------------------------------------------------------
 *  PAUSE-DRAG ENTRY
 *  The runningAnimations entry of a moving o2p. Paused (a
 *  click, the observer) with drag:1, the object can be dragged
 *  freely; on resume the nearest t on the path to where it was
 *  put is handed to resumeAt(t), which moves the loop there
 *  (the two loop kinds do it differently), and kept in
 *  cfg._resumeT for the armed resume's arm_sync.
 * --------------------------------------------------------*/
function createPauseDragEntry(el, cfg, uid, animLoop, resumeAt) {
    const svgOf = () => el.ownerSVGElement || document.querySelector("svg");

    // Dragged while paused: the source goes where it is put (the wrapper's
    // transform is already set when this runs, so read it back in world space)
    function onPauseDrag() {
        if (!cfg.spatial) return;
        emitSpatial(cfg, getWorldPosition(cfg._wrapper), pathElOf(cfg.path, svgOf()));
    }
    // a spatial source is dragged within its circle: measure it first
    function prepareDrag() {
        if (cfg.spatial) spatialCircle(cfg, pathElOf(cfg.path, svgOf()));
    }

    const entry = {
        wasPaused: false,
        onDrag: onPauseDrag,
        prepareDrag,
        pause() {
            animLoop.pause();
            if (cfg.drag) {
                // one pending enable: a second pause replaces it
                clearTimeout(cfg._pauseDragTimer);
                cfg._pauseDragTimer = setTimeout(() => {
                    prepareDrag();
                    enableO2PPauseDrag(el, cfg, {
                        onDrag: onPauseDrag,
                        onDragEnd: (x, y) => { cfg._pauseDragLastPos = { x, y }; }
                    });
                }, 100);
            }
        },
        play() {
            // dragged or not, the pause's drag goes: a running motion is not
            // draggable, and a resume inside the 100 ms enables nothing after it
            clearTimeout(cfg._pauseDragTimer);
            if (cfg._pauseDragLastPos) {
                const pathEl = pathElOf(cfg.path, svgOf());
                if (pathEl) {
                    const nearestT = findClosestTOnPathFast(pathEl, cfg._pauseDragLastPos.x, cfg._pauseDragLastPos.y);
                    resumeAt(nearestT);
                    cfg._resumeT = nearestT;
                }
                cfg._pauseDragLastPos = null;
            }
            disableO2PPauseDrag(uid);
            animLoop.play();
        },
        stop() {
            clearTimeout(cfg._pauseDragTimer);
            disableO2PPauseDrag(uid);
            animLoop.stop();
        },
    };
    entry.resume = entry.play;
    return entry;
}


function startContinuousO2P(el, cfg, virtual, uid) {
    const { dur, loop, startPos, endPos, next, nextOn } = cfg;
    noteEnd(cfg.uid ?? uid, 0);

    const wrapper = ensureO2PWrapper(el);
    cfg._wrapper = wrapper;
    captureOriginalCenter(wrapper, cfg);

    if (el._o2pAnim) el._o2pAnim.stop?.();

    const tMap = makeTMapper(startPos, endPos);

    cfg._oscLastSent = 0;

    // Compute sweep bounds for world coordinate OSC output
    const svg = el.ownerSVGElement || document.querySelector("svg");
    const pathEl = pathElOf(cfg.path, svg);
    if (pathEl) {
        cfg._sweepBounds = computeSweepBounds(pathEl, el);
        cfg._spatialWorld = null;             // measured again on the first emit
    }

    // Drag resume state
    let dragStart = cfg._dragResumeT ?? null;
    if (cfg._dragResumeT != null) delete cfg._dragResumeT;
    let dragTMap = dragStart != null ? makeTMapper(dragStart, endPos) : null;
    const easeCtrl = normalizeEase(cfg.ease);

    // dur:[…] — a prescribed per-stretch speed profile (o2pSchedule.js). One
    // traversal of the path (a lap, where the path is closed) is compiled into
    // ONE duration and ONE piecewise curve, so the loop below goes on counting
    // traversals and animSync goes on exchanging that phase. The profile
    // covers a whole traversal, so a real sub-range (end != 1) or a
    // non-forward mode keeps the single-ramp path.
    const legsOn = Array.isArray(cfg.durList) && cfg.mode === "forward" && endPos === 1;
    if (Array.isArray(cfg.durList) && !legsOn) {
        console.warn(`[o2p] ${cfg.uid}: dur:[…] needs mode:forward with end:1 — using dur:${cfg.dur} instead`);
    }
    if (cfg.at?.length && !Array.isArray(cfg.durList)) {
        console.warn(`[o2p] ${cfg.uid}: at:[…] needs a dur:[…] list to time the segments — ignoring it`);
    }
    if (legsOn) {
        const nSeg = (cfg.at?.length ?? 0) + 1;
        if (cfg.durList.length % nSeg !== 0) {
            console.warn(`[o2p] ${cfg.uid}: ${cfg.durList.length} dur entries over ${nSeg} segments — the list cycles mid-traversal, which is usually a typo`);
        }
    }
    let passIndex = 0;
    const planPass = (from) => compilePass({
        at: cfg.at, durs: cfg.durList, eases: cfg.ease, start: from, pass: passIndex,
    });
    let plan = legsOn ? planPass(dragStart ?? startPos) : null;
    if (plan) easeCtrl.fn = plan.ease;

    // Create animation loop using shared helper
    const animLoop = createAnimationLoop({
        dur: plan ? plan.dur : dur,
        loop,
        getSpeed: () => cfg._held ? 0 : (cfg.speedMultiplier ?? 1),   // _held: scrub

        onTick: (rawPhase, dt) => {
            window._o2p_dt = 0.016; // approximate for rotspeed calculations
            cfg._velClock = (cfg._velClock ?? 0) + dt * 1000;   // the loop's own time, for velocity
            const phase = easeCtrl.fn(rawPhase);

            // Calculate globalT based on mode
            let globalT;
            const effectiveStart = dragStart ?? startPos;
            const effectiveTMap = dragStart != null ? dragTMap : tMap;
            const hasCustomStart = (effectiveStart !== 0);
            const hasCustomEnd = (endPos !== 1);

            if (cfg.mode === "forward") {
                globalT = hasCustomStart && !hasCustomEnd
                    ? (effectiveStart + phase) % 1
                    : effectiveTMap(phase);
            } else if (cfg.mode === "reverse") {
                globalT = hasCustomStart && !hasCustomEnd
                    ? (effectiveStart - phase + 1) % 1
                    : effectiveTMap(1 - phase);
            } else {
                globalT = effectiveTMap(phase);
            }

            const sample = virtual.sample(globalT);
            if (!sample) return;

            const { path, point, pathT } = sample;
            // Cache expensive SVG operations on the path element
            const length = path._cachedLen ?? (path._cachedLen = path.getTotalLength());
            const EPS = 0.1;
            const localL = pathT * length;
            const aheadLen = Math.min(length - EPS, Math.max(0, localL + EPS));
            const ahead = path.getPointAtLength(aheadLen);

            let angle = Math.atan2(ahead.y - point.y, ahead.x - point.x) * (180 / Math.PI);
            if (!Number.isFinite(angle)) angle = 0;

            applyTransform(wrapper, point, angle, cfg, path);
            repositionHitLabelFast(cfg.uid);
            emitO2POsc({ cfg, uid: cfg.uid, path, point, pathT });

            // Control plane publish - cache bbox (static path)
            const bbox = path._cachedBBox ?? (path._cachedBBox = path.getBBox());
            const normX = bbox.width > 0 ? (point.x - bbox.x) / bbox.width : 0;
            const normY = bbox.height > 0 ? (point.y - bbox.y) / bbox.height : 0;
            const vel = velocityChannels("o2p", cfg.uid, cfg, point.x, point.y, length, cfg._velClock);
            publish("o2p", cfg.uid, { t: globalT, x: normX, y: normY, angle, ...vel });

            cfg._currentT = globalT;
            cfg._phase = rawPhase;  // Raw phase for sync (globalT is mapped and can differ)

            if (cfg._overlay) {
                cfg._overlay.update(`x:${point.x.toFixed(1)} y:${point.y.toFixed(1)} v:${vel.vel.toFixed(0)}`);
            }
        },

        onLoopComplete: () => {
            dragStart = null;
            dragTMap = null;
            if (plan) {
                // The next traversal's own seconds and curve. A drag-resumed
                // pass is over, so this one starts from the authored point again.
                passIndex++;
                plan = planPass(startPos);
                easeCtrl.fn = plan.ease;
                animLoop.setDur(plan.dur);
            } else {
                easeCtrl.next();
            }
            if (next && nextOn === "cycle") {
                runNext(next, el);
            }
        },

        onComplete: () => {
            // freeze:1 → rest at the end of the path (A→B and stop at B)
            if (loop !== 0 && !cfg.freeze) positionO2PInitial(el, cfg);
            if (loop !== 0) noteEnd(cfg.uid ?? uid, 1);
            if (loop !== 0 && next && nextOn === "stop") {
                runNext(next, el);
            }
        }
    });

    // Create pause-drag entry
    const pauseDragEntry = createPauseDragEntry(el, cfg, uid, animLoop, (t) => animLoop.setPhase(t));

    window.runningAnimations.set(uid, pauseDragEntry);
    el._o2pAnim = animLoop;
    cfg._anim = pauseDragEntry;

    // ui(uid:…, phase:t) — put the object at path position t (the space of
    // start:/end:), as the loop's raw phase: the inverse of onTick's mapping.
    cfg._seekT = (t) => {
        dragStart = null;
        dragTMap = null;
        const span = endPos - startPos;
        let p;
        if (endPos === 1 && startPos !== 0 && (cfg.mode === "forward" || cfg.mode === "reverse")) {
            p = cfg.mode === "forward" ? t - startPos : startPos - t;
            p = ((p % 1) + 1) % 1;
        } else {
            p = span > 0 ? Math.max(0, Math.min(1, (t - startPos) / span)) : 0;
            if (cfg.mode === "reverse") p = 1 - p;
        }
        animLoop.setPhase(invertEase(easeCtrl.fn, p));
    };

    animLoop.start();
}

/**
 * scrub:1 — press on a running motion and drag: the object follows the
 * pointer along its path (the drag projected onto the path's direction, so a
 * long strip moves under the finger like tape), it is held while held, and
 * it carries on at its speed from where it is let go. Every client sees the
 * hold and the moves (animSync seekAnimation, hold:true while held). A press
 * without a drag runs tap:, if there is one. allow:/lock: apply.
 */
const SCRUB_SLOP_PX = 6;
const SCRUB_SEND_MS = 50;

function installScrub(el, cfg) {
    // A fresh set of listeners for this cfg; a live re-run replaces them
    // (teardownO2P aborts them too).
    el._o2pScrub?.abort();
    el._o2pScrub = new AbortController();
    const { signal } = el._o2pScrub;
    el.style.pointerEvents = "all";
    el.style.cursor = "grab";
    el.style.touchAction = "none";
    let g = null;

    // a screen-pixel drag → a change of path position, along the path's
    // direction at t (the path's own screen CTM carries every zoom and scale)
    const alongPath = (pathEl, t, dx, dy) => {
        const m = pathEl.getScreenCTM();
        const len = pathEl._cachedLen ?? (pathEl._cachedLen = pathEl.getTotalLength());
        if (!m || !(len > 0)) return 0;
        const inv = m.inverse();
        const lx = inv.a * dx + inv.c * dy, ly = inv.b * dx + inv.d * dy;
        const L0 = Math.max(0, Math.min(len - 0.5, t * len));
        const p0 = pathEl.getPointAtLength(L0), p1 = pathEl.getPointAtLength(L0 + 0.5);
        const tx = p1.x - p0.x, ty = p1.y - p0.y, tl = Math.hypot(tx, ty) || 1;
        return (lx * tx + ly * ty) / tl / len;
    };
    const wraps = () => cfg.loop === 0 && cfg.mode !== "alternate";
    const place = (t) => wraps() ? ((t % 1) + 1) % 1 : Math.max(0, Math.min(1, t));

    el.addEventListener("pointerdown", (e) => {
        if (e.button) return;
        e.stopPropagation();
        e.preventDefault();
        if (!el._o2pAnim || !mayOperate(el)) return;
        const pathEl = pathElOf(cfg.path, el.ownerSVGElement);
        if (!pathEl) return;
        g = { x: e.clientX, y: e.clientY, at: performance.now(), t0: cfg._currentT ?? 0, t: null, pathEl, moved: false, sent: 0 };
        try { el.setPointerCapture(e.pointerId); } catch { /* a synthetic pointer */ }
    }, { signal });
    el.addEventListener("pointermove", (e) => {
        if (!g) return;
        const dx = e.clientX - g.x, dy = e.clientY - g.y;
        if (!g.moved && Math.hypot(dx, dy) < SCRUB_SLOP_PX) return;
        if (!g.moved) {
            g.moved = true;
            cfg._scrubbing = true;           // animSync leaves it to the finger
            el.style.cursor = "grabbing";
        }
        g.t = place(g.t0 + alongPath(g.pathEl, g.t0, dx, dy));
        const now = performance.now();
        const send = now - g.sent >= SCRUB_SEND_MS;
        if (send) g.sent = now;
        seekAnimation(cfg.uid, g.t, { hold: true, quiet: !send });
    }, { signal });
    const up = (e) => {
        if (!g) return;
        const { moved, t, t0, at } = g;
        g = null;
        try { el.releasePointerCapture(e.pointerId); } catch { /* already released */ }
        el.style.cursor = "grab";
        if (moved) {
            cfg._scrubbing = false;
            seekAnimation(cfg.uid, t ?? cfg._currentT ?? 0, { hold: false });
            if (cfg.scrubFlip && t != null) flipIfAgainst(cfg, t - t0);
        } else if (e.type === "pointerup" && cfg.tap != null && performance.now() - at < 600) {
            runNext(cfg.tap, el);
        }
    };
    el.addEventListener("pointerup", up, { signal });
    el.addEventListener("pointercancel", up, { signal });
}

/**
 * scrub:flip — a drag against the way the motion runs reverses it, at the
 * same speed: the bipolar fader its speed follows goes to 1−t, as
 * ui(value:flip) does (and is shared the same way). `moved` is the drag in
 * path position; on a loop the short way round counts.
 */
function flipIfAgainst(cfg, moved) {
    if (cfg.loop === 0) moved -= Math.round(moved);
    const speed = cfg.speedMultiplier ?? 0;
    if (!cfg.bipolar || !speed || Math.abs(moved) < 1e-4 || Math.sign(moved) === Math.sign(speed)) return;
    const uid = cfg._durRaw?.source;
    const fader = window.oscillaFaders?.get(uid);
    const now = Number(busGet(`${uid}.t`, NaN));
    if (fader && Number.isFinite(now)) fader.set(1 - now);
}


function startAlternateO2P(el, cfg, virtual, uid) {
    const { dur, loop, startPos, endPos, next, nextOn } = cfg;
    noteEnd(cfg.uid ?? uid, 0);
    const isClosed = virtual.closed;

    // A profile times one traversal in one direction; a there-and-back would
    // need the stretches read backwards on the return. Say so rather than
    // quietly using durList[0] and looking like a typo.
    if (Array.isArray(cfg.durList)) {
        console.warn(`[o2p] ${cfg.uid}: dur:[…] is not supported in mode:alternate — using dur:${dur} instead`);
    }

    const wrapper = ensureO2PWrapper(el);
    cfg._wrapper = wrapper;
    captureOriginalCenter(wrapper, cfg);

    if (el._o2pAnim) el._o2pAnim.stop?.();

    const tMap = makeTMapper(startPos, endPos);

    cfg._oscLastSent = 0;

    // Compute sweep bounds for world coordinate OSC output
    const svg = el.ownerSVGElement || document.querySelector("svg");
    const pathEl = pathElOf(cfg.path, svg);
    if (pathEl) {
        cfg._sweepBounds = computeSweepBounds(pathEl, el);
        cfg._spatialWorld = null;             // measured again on the first emit
    }

    // Drag resume state
    let dragT = cfg._dragResumeT ?? null;
    let isDragResume = dragT != null;
    if (cfg._dragResumeT != null) delete cfg._dragResumeT;
    const easeCtrl = normalizeEase(cfg.ease);

    // Create alternating animation loop using shared helper
    const animLoop = createAlternatingLoop({
        dur,
        loop,
        getSpeed: () => cfg._held ? 0 : (cfg.speedMultiplier ?? 1),   // _held: scrub

        onDirectionChange: (dir) => {
            easeCtrl.next();
            cfg._alternateDir = dir;
            // A resumed run's first leg goes from where it was resumed to the
            // end it was heading for; every leg after that is a whole one. On
            // an open path that is at the first turn -- keeping the resume
            // mapping past it took the way back from the resume point, and
            // the object jumped from the far end to there.
            if (dir === 1 || !isClosed) {
                isDragResume = false;
                dragT = null;
            }
        },

        onTick: (rawPhase, dt, directionSign) => {
            window._o2p_dt = dt;
            cfg._velClock = (cfg._velClock ?? 0) + dt * 1000;
            cfg._alternateDir = directionSign;
            const phase = easeCtrl.fn(rawPhase);

            // Calculate globalT based on direction and drag state
            let globalT;

            if (isDragResume && isClosed) {
                globalT = (dragT + directionSign * phase) % 1;
                if (globalT < 0) globalT += 1;
            } else if (isDragResume) {
                const target = (directionSign > 0) ? endPos : startPos;
                globalT = dragT + (target - dragT) * phase;
            } else if (isClosed) {
                globalT = (startPos + directionSign * phase) % 1;
                if (globalT < 0) globalT += 1;
            } else if (directionSign > 0) {
                globalT = tMap(phase);
            } else {
                globalT = endPos + startPos - tMap(phase);
            }

            const sample = virtual.sample(globalT);
            if (!sample) return;

            const { path, point, pathT } = sample;
            // Cache expensive SVG operations on the path element
            const length = path._cachedLen ?? (path._cachedLen = path.getTotalLength());
            const EPS = 0.1;
            const localL = pathT * length;
            const aheadLen = Math.min(length - EPS, Math.max(0, localL + EPS));
            const ahead = path.getPointAtLength(aheadLen);

            let angle = Math.atan2(ahead.y - point.y, ahead.x - point.x) * (180 / Math.PI);
            if (!Number.isFinite(angle)) angle = 0;

            applyTransform(wrapper, point, angle, cfg, path);
            repositionHitLabelFast(cfg.uid);
            emitO2POsc({ cfg, uid: cfg.uid, path, point, pathT });

            // Control plane publish - cache bbox (static path)
            const bbox = path._cachedBBox ?? (path._cachedBBox = path.getBBox());
            const normX = bbox.width > 0 ? (point.x - bbox.x) / bbox.width : 0;
            const normY = bbox.height > 0 ? (point.y - bbox.y) / bbox.height : 0;
            const vel = velocityChannels("o2p", cfg.uid, cfg, point.x, point.y, length, cfg._velClock);
            publish("o2p", cfg.uid, { t: globalT, x: normX, y: normY, angle, ...vel });

            cfg._currentT = globalT;
            cfg._phase = rawPhase;  // Raw phase for sync (globalT is mapped and can differ)

            if (cfg._overlay) {
                cfg._overlay.update(`x:${point.x.toFixed(1)} y:${point.y.toFixed(1)} v:${vel.vel.toFixed(0)}`);
            }
        },

        onLoopComplete: () => {
            if (next && nextOn === "cycle") {
                runNext(next, el);
            }
        },

        onComplete: () => {
            // freeze:1 → rest at the end of the path (A→B and stop at B)
            if (loop !== 0 && !cfg.freeze) positionO2PInitial(el, cfg);
            if (loop !== 0) noteEnd(cfg.uid ?? uid, 1);
            if (loop !== 0 && next && nextOn === "stop") {
                runNext(next, el);
            }
        }
    });

    // Handle initial direction from drag resume
    const resumeDir = cfg._dragResumeDir ?? 1;
    delete cfg._dragResumeDir;
    if (resumeDir < 0 && isDragResume) {
        animLoop.setDirection(-1);
    }

    // Create pause-drag entry
    // from the drag position: this leg starts there (dragT), at phase 0
    const pauseDragEntry = createPauseDragEntry(el, cfg, uid, animLoop, (t) => {
        dragT = t;
        isDragResume = true;
        animLoop.setPhase(0);
    });

    window.runningAnimations.set(uid, pauseDragEntry);
    el._o2pAnim = animLoop;
    cfg._anim = pauseDragEntry;

    animLoop.start();
}


function startO2PForElement(el, cfg) {

    // Stop previous animation - kill both the animation controller
    // AND the runningAnimations entry to prevent zombie loops.
    {
        const oldUid = cfg.uid || el.dataset?.animUid;
        if (oldUid) {
            const oldEntry = window.runningAnimations?.get(oldUid);
            if (oldEntry?.stop) {
                oldEntry.stop();
            }
        }
    }
    if (el._o2pAnim) {
        el._o2pAnim.pause();
        el._o2pAnim = null;
    }

    cfg.mode = (cfg.mode ?? "forward").toLowerCase();

    // ------------------------------
    // OSC configuration
    // ------------------------------
    const osc = cfg.osc;
    let oscCfg = { enabled: false, throttle: 30, lastSent: 0 };
    const n = Number(osc);

    if (osc === false || osc === "false" || n === 0) {
        oscCfg.enabled = false;
    } else if (osc === true || osc === "true") {
        oscCfg.enabled = true;
    } else if (!Number.isNaN(n)) {
        oscCfg.enabled = true;
        oscCfg.throttle = Math.max(5, n);
    }

    // If oscToggle is enabled (legacy), restore OSC state from localStorage
    // NOTE: toggleStates is decoupled from OSC - doesn't affect oscCfg.enabled
    if (cfg.oscToggle && !cfg.toggleStates) {
        // Legacy behavior: oscToggle gates OSC sending
        if (cfg._oscEnabled === undefined && cfg.uid) {
            if (loadOscState(cfg.uid)?.oscMode) cfg._oscEnabled = true;
        }
        oscCfg.enabled = cfg._oscEnabled ?? false;
    }

    // If toggleStates is defined, restore toggle state (but don't affect OSC)
    if (cfg.toggleStates && cfg.uid) {
        const saved = loadToggleState(cfg.uid);
        if (saved) {
            cfg._toggleStateIndex = saved.stateIndex ?? 0;
            cfg._toggleColor = saved.color || null;
        }
        cfg._toggleStateIndex = cfg._toggleStateIndex ?? 0;
    }

    cfg.oscCfg = oscCfg;

// ------------------------------------------------------
// DEBUG OVERLAY — ONLY if OSC is enabled
// ------------------------------------------------------
const pathEl = pathElOf(cfg.path);

// destroy previous overlay if restarting
if (cfg._overlay) {
    cfg._overlay.destroy();
    cfg._overlay = null;
}

// draw overlay ONLY if OSC sending is enabled
if (pathEl && cfg.oscCfg?.enabled) {
    cfg._overlay = createOscOverlay({
        anchorEl: pathEl,
        label: cfg.oscAddr || cfg.uid
    });

    cfg._overlay.update("...");
    cfg._overlay.position();
}


    // ------------------------------------
    // Resolve path + virtual path wrapper
    // ------------------------------------
    const svg = el.ownerSVGElement || document.querySelector("svg");
    const p = pathElOf(cfg.path, svg);
    if (!p) return;

    const virtual = new VirtualPath([p]);
    if (!virtual.totalLen) return;

    // Detect vertices for polygon paths (for vertex crossing OSC)
    const vertices = polygonVertexTs(p.getAttribute("d"));
    if (vertices) {
        cfg._vertices = vertices;
        cfg._lastT = null;  // Track last t for crossing detection
    }

    const uid = cfg.uid || ("o2p_" + Math.random().toString(36).slice(2));

    if (cfg.mode === "alternate") {
        startAlternateO2P(el, cfg, virtual, uid);
    } else {
        startContinuousO2P(el, cfg, virtual, uid);
    }
}

/* ---------------------------------------------------------
 *  11. positionO2PInitial — Pre-position at start
 * --------------------------------------------------------*/
function positionO2PInitial(el, cfg) {
    try {
        // Get or create wrapper
        const wrapper = ensureO2PWrapper(el);
        cfg._wrapper = wrapper;

        // Capture original center if not done yet
        captureOriginalCenter(wrapper, cfg);

        const pathEl = pathElOf(cfg.path);
        if (!pathEl) {
            console.warn("[o2p] Initial position: path not found:", cfg.path);
            return;
        }

        const length = getPathLength(pathEl);
        const t = cfg.startPos ?? 0;

        const point = getPointOnPath(pathEl, t);

        // Compute tangent angle
        // For simple lines, calculate from endpoints directly
        const line = parseSimpleLine(pathEl);
        let angle;
        if (line) {
            angle = Math.atan2(line.y2 - line.y1, line.x2 - line.x1) * (180 / Math.PI);
        } else {
            const localL = t * length;
            const EPS = 0.1;
            const aheadLen = Math.min(length - EPS, Math.max(0, localL + EPS));
            const ahead = pathEl.getPointAtLength(aheadLen);
            angle = Math.atan2(ahead.y - point.y, ahead.x - point.x) * (180 / Math.PI);
        }
        if (!isFinite(angle)) angle = 0;

        // Apply to WRAPPER
        applyTransform(wrapper, point, angle, cfg, pathEl);
        repositionAllHitLabels();

    } catch (err) {
        console.error("[o2p] Error in positionO2PInitial:", err);
    }
}


/* ---------------------------------------------------------
 *  12. Cue handler: handleO2PCue
 * --------------------------------------------------------*/
/** Where an o2p's object is along its path now, 0..1, or null. */
function liveT(cfg) {
    if (Number.isFinite(cfg?._currentT)) return cfg._currentT;
    const bus = Number(busGet(`${cfg?.uid}.t`, NaN));
    return Number.isFinite(bus) ? bus : null;
}

/**
 * Stop an o2p and take down what it hung on the element -- its motion, its
 * touch fader's hit label and drag handler, its scrub listeners -- so the
 * element can be set up again, in another mode if need be.
 */
function teardownO2P(el, cfg) {
    try { el._o2pAnim?.stop?.(); } catch { /* already stopped */ }
    el._o2pAnim = null;
    try { window.runningAnimations?.get(cfg.uid)?.stop?.(); } catch { /* already stopped */ }
    window.runningAnimations?.delete(cfg.uid);
    cfg._touchModeActive = false;
    el._touchModeInitialized = false;
    el._o2pScrub?.abort();
    el._o2pScrub = null;
    el._o2pTouch?.abort();
    el._o2pTouch = null;
    cfg._overlay?.destroy?.();
    cfg._overlay = null;
    window.oscillaFaders?.delete(cfg.uid);   // ui(value:) must not drive a dead fader
    destroyHitLabel(cfg.uid);
}

/**
 * Take an element's o2p down entirely, leaving the object where it is (a
 * made shape being removed, or o2p(off:1)). Its cfg is found in the
 * registry: el._oscillaCfg is the last cue's, a rotate run after the o2p.
 */
export function releaseO2P(el) {
    if (!el) return;
    const entry = Object.values(window.oscillaAnimRegistry || {}).find(e => e?.el === el && e.kind === "o2p");
    const cfg = entry?.cfg || (el._o2pAnim || el._touchModeInitialized ? el._oscillaCfg : null);
    if (cfg) teardownO2P(el, cfg);
}

export function handleO2PCue(el, args, options = {}) {
    const { fromCueTrigger = false } = options;

    try {
        if (!Array.isArray(args)) args = [];

        // -----------------------------------------------------------
        // CHECK: Is this a playhead trigger for already-registered element?
        // -----------------------------------------------------------
        const existingCfg = el._oscillaCfg;

        // -----------------------------------------------------------
        // LIVE RE-RUN: the live console runs an o2p again on an element
        // that has one. It carries on from where the object is, with the
        // new parameters: a moving object takes them on the move, trig:touch
        // stops it there as a fader (and trig:auto sets a fader going from
        // where it was left), loop:1 runs it out from there. Playhead and
        // button re-triggers restart from start: as they always have.
        // -----------------------------------------------------------
        if (options.live && existingCfg && !existingCfg._armed) {
            const resume = { t: liveT(existingCfg), dir: existingCfg._alternateDir ?? 1 };
            teardownO2P(el, existingCfg);
            return handleO2PCue(el, args, {
                fromCueTrigger, resume: resume.t != null ? resume : null,
            });
        }
        const resume = options.resume || null;

        if (fromCueTrigger && existingCfg && existingCfg._armed) {
            const paramsChanged = diffAndMergeParams(existingCfg, args);

            if (!paramsChanged) {
                // Playhead re-trigger with same params — no-op if running
                armElement(el, existingCfg);
                if (existingCfg.trig === "edge" && !existingCfg._running) {
                    startO2PForElement(el, existingCfg);
                    existingCfg._running = true;
                }
                return;
            }

            // Live console edit: params already merged by diffAndMergeParams.
            // If running, restart from current position on next tick.
            existingCfg._needsRestart = true;

            if (existingCfg._armState === "running") {
                const restartT = existingCfg._currentT;
                setTimeout(() => {
                    if (!existingCfg._needsRestart) return;
                    delete existingCfg._needsRestart;
                    if (restartT != null && Number.isFinite(restartT)) {
                        existingCfg._dragResumeT = restartT;
                    }
                    existingCfg._armState = "running";
                    if (typeof existingCfg._start === "function") existingCfg._start();
                }, 0);
            }

            return;
        }

        // -----------------------------------------------------------
        // FULL SETUP (first time registration)
        // -----------------------------------------------------------
        const cfg = {
            path: null,
            mode: "forward",

            dur: 1,
            durList: null,       // dur:[…] — beats per segment, held as seconds (o2pSchedule.js)
            at: null,            // at:[…] — where a traversal is cut into segments
            loop: 0,

            rotate: 0,
            rotoffset: 0,
            rotlock: 0,
            rotspeed: 0,
            rotdir: 1,

            ease: "linear",
            osc: false,
            oscToggle: false,

            startPos: 0,
            endPos: 1,

            start: 0,            // tdelay:, in seconds (scheduleCueStart)
            init: "show",

            uid: null,
            next: null,
            nextOn: null,
            trig: "auto",

            kind: "o2p",

            // Preset/group system
            group: null,         // Group ID for preset grouping
            handle: null,        // SVG element ID for user-supplied rotation handle
            hmode: null,         // "limited" | "continuous" | null (no rotation)
            rotrange: null,      // Degrees of rotation range
            drag: false,         // Enable pause-drag repositioning (spatial scores)

            astArgs: args,
            fromCueTrigger
        };

        // -----------------------------------------------------------
        // Parse DSL arguments
        // -----------------------------------------------------------
        const k = secPerBeat();     // dur: and tdelay: are beats
        for (const a of args) {
            const key = a.type;
            const val = a.value;

            switch (key) {
                case "path": cfg.path = val; break;

                case "mode":
                    cfg.mode = String(val).toLowerCase();
                    break;

                case "dur": {
                    // A list is BEATS PER SEGMENT (see at:, and o2pSchedule.js)
                    // — a prescribed speed profile across the traversal. Anything
                    // else is one number for the whole pass, possibly a signalRef.
                    // Turned to seconds here; _durRaw keeps the seconds form.
                    const d = secs(val, k);
                    if (Array.isArray(d)) {
                        cfg.durList = d.map(x => (Number(x) > 0 ? Number(x) : 1));
                        cfg.dur = cfg.durList[0];
                    } else {
                        cfg._durRaw = d;
                        cfg.dur = isSignalRef(d) ? (d.range?.[0] ?? 1) : (Number(d) || 1);
                    }
                    break;
                }

                // at:[0.25, 0.5, 0.75] — where the traversal is cut into
                // stretches. Absolute path positions, the same space as
                // start:/end:, so every object on this path changes speed at the
                // same places whatever its own start offset.
                case "at":
                    cfg.at = (Array.isArray(val) ? val : [val])
                        .map(Number).filter(v => Number.isFinite(v) && v > 0 && v < 1)
                        .sort((x, y) => x - y);
                    break;

                case "bipolar":
                    // Enable bipolar speed mode: center=stopped, left=reverse, right=forward
                    cfg.bipolar = val === "1" || val === "true" || val === true || Number(val) === 1;
                    break;

                case "loop":
                    cfg.loop = Number(val) || 0;
                    break;

                // freeze:1 (or fill:freeze) — after a bounded loop finishes,
                // stay at the END of the path instead of snapping back to A
                case "freeze":
                    cfg.freeze = truthy(val);
                    break;
                case "fill":
                    cfg.freeze = String(val).toLowerCase().trim() === "freeze";
                    break;

                case "rotate":
                    if (typeof val === "string") {
                        const v = val.toLowerCase().trim();
                        if (/^-?\d+(\.\d+)?$/.test(v)) cfg.rotate = Number(v);
                        else cfg.rotate = v;
                    } else {
                        cfg.rotate = val;
                    }
                    break;

                case "rotoffset": cfg.rotoffset = Number(val) || 0; break;
                case "rotlock": cfg.rotlock = Number(val) || 0; break;
                case "rotspeed":
                    // Store raw value - may be signalRef for dynamic binding
                    cfg._rotspeedRaw = val;
                    cfg.rotspeed = isSignalRef(val) ? (val.range?.[0] ?? 0) : (Number(val) || 0);
                    break;
                case "rotdir": cfg.rotdir = Number(val) || 1; break;

                case "ease": cfg.ease = val; break;
                case "osc": {
                    let v = val;

                    // normalize booleans + numeric strings
                    if (typeof v === "string") {
                        const lower = v.toLowerCase();

                        if (lower === "true") v = true;
                        else if (lower === "false") v = false;
                        else if (!isNaN(lower)) v = Number(lower);
                    }

                    if (v == null) v = false;

                    cfg.osc = v;
                    break;
                }

                case "oscaddr":
                case "oscAddr": {
                    if (typeof val === "string") {
                        cfg.oscAddr = val;
                    }

                    break;
                }

                case "oscToggle":
                case "osctoggle": {
                    cfg.oscToggle = truthy(val);
                    break;
                }

                case "toggleStates":
                case "togglestates": {
                    // Parse "toggleStates:[idle, claimed]" or "toggleStates:idle,claimed"
                    if (typeof val === "string") {
                        const parts = val.replace(/[\[\]]/g, "").split(",").map(v => v.trim()).filter(v => v);
                        if (parts.length >= 1) {
                            cfg.toggleStates = parts;
                        }
                    } else if (Array.isArray(val)) {
                        cfg.toggleStates = val.map(String);
                    }
                    break;
                }

                case "toggleColors":
                case "togglecolors": {
                    // Parse "toggleColors:[none, #client]" or "toggleColors:none,#ff0000"
                    if (typeof val === "string") {
                        const parts = val.replace(/[\[\]]/g, "").split(",").map(v => v.trim()).filter(v => v);
                        if (parts.length >= 1) {
                            cfg.toggleColors = parts;
                        }
                    } else if (Array.isArray(val)) {
                        cfg.toggleColors = val.map(String);
                    }
                    break;
                }

                case "onToggle":
                case "ontoggle": {
                    // Cue expression to run on toggle state change
                    cfg.onToggle = String(val);
                    break;
                }

                case "start": cfg.startPos = startVal(cfg, val); break;
                case "end": cfg.endPos = Number(val); break;

                // tdelay: the wait before it starts, as scheduleCueStart reads
                // it (cfg.start; start: is a place on the path, startPos). It
                // was read into a startDelay nothing used. A live re-run that
                // carries on from where the object is does not wait again.
                case "tdelay": cfg.start = resume ? 0 : (Number(secs(val, k)) || 0); break;

                case "init": cfg.init = val; break;

                case "uid": cfg.uid = val; break;
                case "sync": cfg.sync = !isOff(val); break;
                case "next": cfg.next = val; break;
                case "nextOn": cfg.nextOn = val; break;

                case "trig":
                    cfg.trig = String(val).toLowerCase();
                    break;

                // Preset/group system
                case "group":
                    cfg.group = String(val);
                    break;

                case "launcher":
                    cfg.launcher = (val === "false" || val === "0") ? false : true;
                    break;

                case "handle":
                    cfg.handle = String(val);
                    break;

                case "hmode":
                    cfg.hmode = normalizeHmodeValue(String(val));
                    break;

                case "rotrange":
                    cfg.rotrange = Number(val) || null;
                    break;

                case "drag":
                    cfg.drag = truthy(val);
                    break;

                // spatial:plan|dome -- the object is a sound source in space:
                // position on the bounds: circle -> azimuth / elevation /
                // distance, sent as format: (aed, ae, ad, xyz, aedg, raw)
                case "spatial": {
                    const m = String(val).toLowerCase();
                    cfg.spatial = (m === "dome") ? "dome" : (m === "0" || m === "false" || m === "none") ? null : "plan";
                    break;
                }
                case "bounds": cfg.bounds = String(val); break;
                case "format": cfg.spatialFormat = String(val).toLowerCase(); break;
                case "elev":   cfg.elev = Number(val) || 0; break;

                // scrub:1 — grab the object and drag it along its path (see
                // installScrub); tap:"<cue>" — what a press without a drag does
                case "anchor":
                    cfg.anchor = Array.isArray(val) ? val.map(Number) : String(val).toLowerCase();
                    break;

                case "scrub":
                    cfg.scrub = truthy(val) || val === "flip";
                    cfg.scrubFlip = val === "flip";
                    break;
                case "tap":
                    cfg.tap = val;
                    break;

                // hit:0 — no hit label: nothing to click on the object. For a
                // motion nobody clicks, above all a large one, whose label
                // (sized to the object) would cover the controls around it.
                case "hit":
                    cfg.hit = !(val === false || val === "false" || val === 0 || val === "0");
                    break;

                // Target button parameters (for controlling other faders)
                case "target":
                    cfg.target = String(val);
                    break;

                case "targetT":
                case "targett":
                    cfg.targetT = Number(val);
                    break;

                case "toggle":
                    // Parse "toggle:0,0.8" or "toggle:[0,0.8]"
                    if (typeof val === "string") {
                        const parts = val.replace(/[\[\]]/g, "").split(",").map(v => Number(v.trim()));
                        if (parts.length >= 2 && parts.every(n => !isNaN(n))) {
                            cfg.toggle = parts;
                        }
                    } else if (Array.isArray(val)) {
                        cfg.toggle = val.map(Number);
                    }
                    break;
            }
        }

        // -----------------------------------------------------------
        // Normalisation & guards
        // -----------------------------------------------------------
        if (cfg.startPos === "auto") {
            cfg._autoStartT = resolveAutoStart(el, cfg.path);
            cfg.startPos = cfg._autoStartT;
        }
        if (!Number.isFinite(cfg.startPos)) cfg.startPos = 0;
        if (!Number.isFinite(cfg.endPos)) cfg.endPos = 1;

        cfg.startPos = Math.max(0, Math.min(1, cfg.startPos));
        cfg.endPos = Math.max(0, Math.min(1, cfg.endPos));

        cfg.mode = normalizeMode(cfg.mode);

        // Resolve rotation handle config
        if (cfg.hmode) {
            cfg.rotrange = parseRotRange(cfg.rotrange, cfg.hmode);
        }

        if (!cfg.uid) {
            cfg.uid = "o2p_" + Math.random().toString(36).slice(2, 10);
        }

        // -----------------------------------------------------------
        // Set up dynamic parameter bindings (dur, rotspeed)
        //
        // Both helpers own their binding by "<kind>:<uid>:<param>", so
        // re-running this cue releases the previous one rather than leaving
        // a live subscription writing to a stale cfg.
        // -----------------------------------------------------------

        // Speed binding for dur
        if (cfg._durRaw) {
            // Bipolar maxSpeed derives from the cue's stated dur range, so
            // the range holds at the fader extremes — a wider range (e.g.
            // 120→2) gives faster extremes, a narrower range (120→90)
            // gentler ones.
            const durRange = cfg._durRaw?.range;
            const computedMaxSpeed = (durRange && durRange[1] > 0)
                ? durRange[0] / durRange[1]
                : 120;
            setupSpeedBinding(cfg, cfg._durRaw, {
                kind: 'o2p',
                baseDur: cfg.dur,
                minDur: 0.5,
                maxDur: 120,
                minSpeed: 1,
                maxSpeed: computedMaxSpeed,
                bipolar: cfg.bipolar
            });
        }

        // Rotspeed binding
        if (cfg._rotspeedRaw && isSignalRef(cfg._rotspeedRaw)) {
            setupParamBinding(cfg, 'rotspeed', cfg._rotspeedRaw, {
                min: 0,
                max: 30,
                default: cfg.rotspeed
            });
        }

        if (!cfg.path) {
            console.warn("[o2p] Missing path argument.");
            return;
        }
        // From the live console the path must be there now -- say which and
        // how to make one. (A score's path may arrive later, with its page.)
        if (options.live && !pathElOf(cfg.path, el.ownerSVGElement)) {
            console.warn(`[o2p] path:${cfg.path} — nothing by that name to follow: make it first, e.g. shape(uid:${cfg.path}, sides:1)`);
            return;
        }

        // -----------------------------------------------------------
        // STORE CFG ON ELEMENT
        // -----------------------------------------------------------
        el._oscillaCfg = cfg;

        const isScrollMode = window.mode === "scroll";
        // trig:<uid> waits for its source object (probeTriggers) even in page mode
        const shouldStartNow =
            cfg.trig !== "touch" && (
                fromCueTrigger ||
                ((cfg.trig === "auto" || !isScrollMode) && !isObjectTrig(cfg.trig))
            );

        // -----------------------------------------------------------
        // Skip re-initialization for touch mode elements
        // This prevents positionO2PInitial from overwriting synced positions
        // -----------------------------------------------------------
        if (cfg.trig === "touch" && el._touchModeInitialized) {
            return;
        }

        // -----------------------------------------------------------
        // PRESTATE BEFORE START (REGISTRATION)
        // -----------------------------------------------------------
        applyPrestateBeforeStart(el, cfg);

        // Install double-click OSC toggle handler (legacy)
        installOscToggleHandler(el, cfg);
        // Install toggle states handler (decoupled from OSC)
        installToggleStatesHandler(el, cfg);

        el.parentNode?.appendChild(el);

        // -----------------------------------------------------------
        // Pre-position object along the path -- where it already was, on a
        // live re-run (startPos stays the start of its range)
        // -----------------------------------------------------------
        if (resume) {
            const rangeStart = cfg.startPos;
            cfg.startPos = resume.t;
            positionO2PInitial(el, cfg);
            cfg.startPos = rangeStart;
            cfg._currentT = resume.t;
            if (cfg.trig !== "touch") {
                cfg._dragResumeT = resume.t;       // startO2PForElement starts from here
                cfg._dragResumeDir = resume.dir;   // and, alternating, the same way
            }
        } else {
            positionO2PInitial(el, cfg);
        }

        // -----------------------------------------------------------
        // Real animation start function
        // -----------------------------------------------------------
        const rawStart = () => {
            startO2PForElement(el, cfg);
        };

        cfg._start = rawStart;

        // Reset hook for animation.js: o2p positions via a transform
        // ATTRIBUTE on its own wrapper, which the generic style-clearing
        // reset never touches — so put the object back at startPos here.
        cfg._reset = () => {
            el._o2pAnim?.stop?.();
            el._o2pAnim = null;
            window.runningAnimations?.delete(cfg.uid);
            delete cfg._dragResumeT;
            cfg._currentT = cfg.startPos ?? 0;
            positionO2PInitial(el, cfg);
        };

        cfg._applyPrestateOnStart = () =>
            applyPrestateOnStart(el, cfg);

        // -----------------------------------------------------------
        // TOUCH MODE: Interactive drag-to-control mode
        // Handle this BEFORE registerAnimation to prevent any auto-start
        // Touch mode is IMMEDIATELY active - no playhead required
        // -----------------------------------------------------------
        if (cfg.trig === "touch") {
            // Prevent multiple initializations
            if (el._touchModeInitialized) {
                return;
            }
            el._touchModeInitialized = true;
            // The fader's listeners on window and on the element go with it
            // (teardownO2P aborts this), or a re-run would answer every
            // remote move and OSC message once per run
            el._o2pTouch?.abort();
            el._o2pTouch = new AbortController();
            const touchSignal = el._o2pTouch.signal;

            // Make element immediately visible (bypass init system)
            el.style.opacity = "1";
            el.style.visibility = "visible";
            el.style.pointerEvents = "all";

            // Create hit label first (needed for drag handling)
            createHitLabel(el, "o2p", cfg.uid, {
                anchorMode: "object",
                color: "orange",  // Different color for touch mode
                sizeMode: "follow",
                isTouchMode: true,  // Enable value label
                oscToggle: cfg.oscToggle,
                toggleStates: cfg.toggleStates,
                toggleColors: cfg.toggleColors,
                onToggle: cfg.onToggle
            });

            // Get the hit label record we just created
            const hitRecord = window._oscillaHitLabels?.find(r => r.uid === cfg.uid);
            
            // Get the path element
            const svg = el.ownerSVGElement || document.querySelector("svg");
            
            const pathEl = pathElOf(cfg.path, svg);

            if (!pathEl) {
                console.warn("[o2pCue] Touch mode: path not found:", cfg.path);
                return;
            }

            // Get wrapper for transforms
            const wrapper = ensureO2PWrapper(el);
            cfg._wrapper = wrapper;
            captureOriginalCenter(wrapper, cfg);

            // Set up OSC configuration for touch mode
            const osc = cfg.osc;
            let oscCfg = { enabled: false, throttle: 30, lastSent: 0 };
            const n = Number(osc);

            if (osc === false || osc === "false" || n === 0) {
                oscCfg.enabled = false;
            } else if (osc === true || osc === "true") {
                oscCfg.enabled = true;
            } else if (!Number.isNaN(n)) {
                oscCfg.enabled = true;
                oscCfg.throttle = Math.max(5, n);
            }

            // If oscToggle is enabled (legacy), restore OSC state from localStorage
            // NOTE: toggleStates is decoupled from OSC - doesn't affect oscCfg.enabled
            if (cfg.oscToggle && !cfg.toggleStates) {
                // Legacy behavior: oscToggle gates OSC sending
                if (cfg._oscEnabled === undefined && cfg.uid) {
                    if (loadOscState(cfg.uid)?.oscMode) cfg._oscEnabled = true;
                }
                oscCfg.enabled = cfg._oscEnabled ?? false;
            }

            // If toggleStates is defined, restore toggle state (but don't affect OSC)
            if (cfg.toggleStates && cfg.uid) {
                const saved = loadToggleState(cfg.uid);
                if (saved) {
                    cfg._toggleStateIndex = saved.stateIndex ?? 0;
                    cfg._toggleColor = saved.color || null;
                }
                cfg._toggleStateIndex = cfg._toggleStateIndex ?? 0;
            }

            cfg.oscCfg = oscCfg;

            // Create OSC overlay if enabled
            if (pathEl && cfg.oscCfg?.enabled) {
                cfg._overlay = createOscOverlay({
                    anchorEl: pathEl,
                    label: cfg.oscAddr || cfg.uid
                });
                cfg._overlay.update("drag to control");
                cfg._overlay.position();
            }

            // Create the position update function that will be called during drag
            let _o2pLastSync = 0;
            let _o2pTrailing = null;
            let _o2pRemote = false;
            // what ui() moves (ui.js setFaderValue); held: see value:hold
            const faderApi = { set: (t) => updatePosition(t, t), held: null };

            const updatePosition = (mappedT, rawT) => {
                // Cache length on path element for touch mode
                const length = pathEl._cachedLen ?? (pathEl._cachedLen = getPathLength(pathEl));
                const point = getPointOnPath(pathEl, mappedT);

                // Compute tangent angle
                // For simple lines, calculate from endpoints directly (cache line parse result)
                const line = pathEl._cachedLine ?? (pathEl._cachedLine = parseSimpleLine(pathEl));
                let angle;
                if (line) {
                    angle = Math.atan2(line.y2 - line.y1, line.x2 - line.x1) * (180 / Math.PI);
                } else {
                    const localL = mappedT * length;
                    const EPS = 0.1;
                    const aheadLen = Math.min(length - EPS, Math.max(0, localL + EPS));
                    const ahead = pathEl.getPointAtLength(aheadLen);
                    angle = Math.atan2(ahead.y - point.y, ahead.x - point.x) * (180 / Math.PI);
                }
                if (!isFinite(angle)) angle = 0;

                // Apply transform to move object
                applyTransform(wrapper, point, angle, cfg, pathEl);
                cfg._currentT = mappedT;
                repositionHitLabelFast(cfg.uid);

                // Update the value label with current position
                updateHitLabelValue(cfg.uid, rawT);

                // ========== CONTROL PLANE PUBLISH ==========
                // Cache bbox on path element (static path)
                const bbox = pathEl._cachedBBox ?? (pathEl._cachedBBox = pathEl.getBBox());
                const normX = bbox.width > 0 ? (point.x - bbox.x) / bbox.width : 0;
                const normY = bbox.height > 0 ? (point.y - bbox.y) / bbox.height : 0;
                
                const vel = velocityChannels("o2p", cfg.uid, cfg, point.x, point.y, length);
                const publishData = {
                    t: mappedT,
                    x: normX,
                    y: normY,
                    angle: angle,
                    ...vel
                };

                // Include current rotation value if rotation handle is active
                if (cfg.hmode && faderEntry) {
                    publishData.p = faderEntry.curP;
                }

                publish("o2p", cfg.uid, publishData);

                // Emit OSC if enabled
                if (cfg.oscCfg?.enabled) {
                    emitO2POsc({
                        cfg,
                        uid: cfg.uid,
                        path: pathEl,
                        point,
                        pathT: mappedT,
                        rotationP: (cfg.hmode && faderEntry) ? faderEntry.curP : 0
                    });

                    // Update overlay
                    if (cfg._overlay) {
                        cfg._overlay.update(
                            `t:${rawT.toFixed(2)} x:${point.x.toFixed(1)} y:${point.y.toFixed(1)} v:${vel.vel.toFixed(0)}`
                        );
                    }
                }

                // Store current position for external access
                cfg._currentT = mappedT;
                cfg._currentPoint = point;
                cfg._currentAngle = angle;

                // Update fader entry in group registry
                if (faderEntry) {
                    faderEntry.curT = mappedT;
                }

                // Broadcast position to other clients (throttled)
                // In composition/ensemble mode, manual fader moves stay local
                // Only preset recalls broadcast state changes
                const presetScope = window.presetScope || 'user';
                if (!_o2pRemote && presetScope !== 'composition') {
                    // Throttled, but the last value always goes out (a
                    // trailing send): a fader stopped at 0.5 must arrive as 0.5
                    const now = performance.now();
                    clearTimeout(_o2pTrailing);
                    if (now - _o2pLastSync >= 50) {
                        _o2pLastSync = now;
                        sendPosSync(mappedT);
                    } else {
                        _o2pTrailing = setTimeout(() => {
                            _o2pLastSync = performance.now();
                            sendPosSync(cfg._currentT);
                        }, 50 - (now - _o2pLastSync));
                    }
                }
            };

            const sendPosSync = (t) => {
                const sock = window.socket;
                if (!sock || sock.readyState !== WebSocket.OPEN) return;
                const msg = { type: "pos_sync", kind: "o2p", uid: cfg.uid, t };
                // Only include oscEnabled for elements with oscToggle
                if (cfg.oscToggle) {
                    msg.oscEnabled = cfg.oscCfg?.enabled ?? false;
                }
                // the speed a ui(value:hold) stopped, so any client can release it
                msg.held = faderApi.held ?? null;
                sock.send(JSON.stringify(msg));
            };

            // -----------------------------------------------------------
            // Group registry fader entry (referenced by updatePosition and rotation handler)
            // Declared before drag handler so updatePosition can access it
            // -----------------------------------------------------------
            let faderEntry = null;

            // -----------------------------------------------------------
            // LATE-JOIN SYNC CHECK - MUST happen BEFORE registerFaderUpdateCallback
            // to prevent default position from being sent to server
            // -----------------------------------------------------------
            if (window._posSyncSnapshot?.[cfg.uid]) {
                const posData = window._posSyncSnapshot[cfg.uid];
                if (posData.kind === "o2p") {
                    _o2pRemote = true;
                    cfg._initialValueSent = true;  // Mark as sent BEFORE registerFaderUpdateCallback
                    if (posData.held !== undefined) faderApi.held = posData.held;
                    updatePosition(posData.t, posData.t);
                    _o2pRemote = false;
                    applyOscSyncState(cfg, posData.oscEnabled, posData.oscColor);
                }
            }

            // -----------------------------------------------------------
            // Register fader update callback for default positioning
            // This allows other cues to set this fader's default position
            // Skip if late-join sync already set the position
            // -----------------------------------------------------------
            registerFaderUpdateCallback(cfg.uid, (defaultT) => {
                if (cfg._initialValueSent) return;  // Late-join sync already applied
                cfg._initialValueSent = true;
                updatePosition(defaultT, defaultT);
            });

            // A cue can move this fader as if by hand: ui(uid:<fader>, value:0.5, dur:4)
            // (ui.js) — the handle moves, the value is published and shared
            (window.oscillaFaders ||= new Map()).set(cfg.uid, faderApi);

            // Initialize the drag handler
            if (hitRecord) {
                initO2PDragHandler(hitRecord, pathEl, cfg, updatePosition);
            } else {
                console.warn("[o2pCue] Touch mode: hit label not found for drag handler", cfg.uid);
            }

            // OSC in at /<uid>/t (say an expression pedal) moves the handle as
            // well as the bus value, on every client, since each receives it;
            // the leader alone shares it, for clients that join later
            window.addEventListener("oscilla:osc-in", (e) => {
                if (e.detail?.address !== `/${cfg.uid}/t`) return;
                const v = Number(e.detail.args?.[0]);
                if (!Number.isFinite(v)) return;
                const t = Math.max(0, Math.min(1, v));
                _o2pRemote = !isLeader();
                updatePosition(t, t);
                _o2pRemote = false;
            }, { signal: touchSignal });

            // Listen for remote position updates from other clients
            // In composition/ensemble mode, ignore manual fader moves from others
            // Only preset recalls should change state
            window.addEventListener("oscilla:pos-sync", (e) => {
                const presetScope = window.presetScope || 'user';
                if (presetScope === 'composition') return; // Ignore manual moves in ensemble mode

                const d = e.detail;
                if (d.kind !== "o2p" || d.uid !== cfg.uid) return;
                if (d.held !== undefined) faderApi.held = d.held;
                _o2pRemote = true;
                updatePosition(d.t, d.t);
                _o2pRemote = false;
                applyOscSyncState(cfg, d.oscEnabled, d.oscColor);
            }, { signal: touchSignal });

            // Listen for local OSC toggle and broadcast immediately (only for oscToggle elements)
            // In composition mode, manual toggles stay local (only preset recalls sync)
            if (cfg.oscToggle) {
                el.addEventListener("oscilla-osc-toggle", (e) => {
                    if (e.detail?.uid !== cfg.uid) return;
                    const oscEnabled = e.detail?.oscEnabled ?? false;
                    cfg._oscEnabled = oscEnabled;
                    if (cfg.oscCfg) cfg.oscCfg.enabled = oscEnabled;

                    // Immediately broadcast the toggle state (skip in composition mode)
                    const presetScope = window.presetScope || 'user';
                    if (presetScope === 'composition') return;

                    const sock = window.socket;
                    if (sock && sock.readyState === WebSocket.OPEN) {
                        const oscColor = window.localClientColor || localStorage.getItem("clientColor") || "#ffaa00";
                        sock.send(JSON.stringify({
                            type: "pos_sync", kind: "o2p",
                            uid: cfg.uid, t: cfg._currentT ?? 0,
                            oscEnabled, oscColor
                        }));
                    }
                }, { signal: touchSignal });
            }

            // -----------------------------------------------------------
            // GROUP REGISTRATION + ROTATION HANDLE SETUP
            // -----------------------------------------------------------
            if (cfg.group) {
                window._o2pTouchGroups = window._o2pTouchGroups || {};
                const group = window._o2pTouchGroups[cfg.group] = window._o2pTouchGroups[cfg.group] || {
                    groupId: cfg.group,
                    faders: {},

                    captureState() {
                        const state = {};
                        for (const [id, f] of Object.entries(this.faders)) {
                            const entry = { t: f.curT };
                            if (f.hmode) entry.p = f.curP;
                            state[id] = entry;
                        }
                        return state;
                    },

                    setPosition(faderId, t, p) {
                        const f = this.faders[faderId];
                        if (!f) return;
                        const start = f.cfg.startPos ?? 0;
                        const end = f.cfg.endPos ?? 1;
                        const range = end - start;
                        const clamped = Math.max(start, Math.min(end, t));
                        const rawT = range > 0 ? (clamped - start) / range : 0;
                        f.updatePosition(clamped, rawT);
                        f.curT = clamped;
                        if (f.hmode && typeof p === 'number') {
                            const angleDeg = denormalizeRotation(p, f.hmode, f.rotrange);
                            const constrained = constrainRotation(angleDeg, f.hmode, f.rotrange);
                            f.curAngle = constrained;
                            f.curP = normalizeRotation(constrained, f.hmode, f.rotrange);
                            // Update visual indicator
                            if (f.rotationRing) {
                                updateRotationIndicator(f.rotationRing, constrained);
                            }
                            if (f.userHandleEl) {
                                updateO2PUserHandle(f, constrained);
                            }
                        }
                    },

                    applyPositions(positions) {
                        for (const [faderId, pos] of Object.entries(positions)) {
                            this.setPosition(faderId, pos.t, pos.p);
                        }
                    }
                };

                const faderId = el.id || cfg.uid;
                faderEntry = {
                    uid: cfg.uid,
                    cfg,
                    pathEl,
                    wrapper,
                    updatePosition,
                    rotationRing: null,
                    userHandleEl: null,
                    curT: cfg._currentT ?? cfg.startPos ?? 0,
                    curP: 0,
                    curAngle: 0,
                    hmode: cfg.hmode,
                    rotrange: cfg.rotrange
                };

                group.faders[faderId] = faderEntry;

                // Create launcher bar for this group (once per group)
                // Delay creation to ensure all faders in the group are registered
                // so the bbox includes all faders and the circle appears immediately
                if (!group._launcherCreated && cfg.launcher !== false) {
                    group._launcherCreated = true;
                    setTimeout(() => {
                        import('../control/o2pLauncher.js').then(({ createO2PLauncher, getGroupBBox }) => {
                            const svgRoot = el.ownerSVGElement || document.querySelector("svg");
                            const bbox = getGroupBBox(cfg.group);
                            if (bbox && svgRoot) {
                                createO2PLauncher(cfg.group, bbox, svgRoot);
                            } else {
                                console.warn(`[o2pCue] Could not create launcher for group "${cfg.group}" - no bbox`);
                            }
                        }).catch(err => {
                            console.warn(`[o2pCue] o2pLauncher.js not available:`, err.message);
                        });
                    }, 300);

                    // Create utility presets ("off" and "on") after a short delay
                    // to ensure all faders in the group are registered
                    // Note: assignToSlots:false since launcher has dedicated on/off button
                    setTimeout(() => {
                        import('../control/o2pPresets.js').then(({ createUtilityPresets }) => {
                            createUtilityPresets(cfg.group, { assignToSlots: false });
                        });
                    }, 500);

                }

            }

            // -----------------------------------------------------------
            // ROTATION HANDLE SETUP (dual-mode: user-supplied or auto-generated)
            // -----------------------------------------------------------
            if (cfg.hmode && hitRecord) {
                const svg = el.ownerSVGElement || document.querySelector("svg");
                let rotationHandleEl = null;
                let rotationRingData = null;

                if (cfg.handle) {
                    // User-supplied rotation handle element
                    rotationHandleEl = svg?.getElementById(cfg.handle);
                    if (!rotationHandleEl) {
                        console.warn(`[o2pCue] Rotation handle element not found: "${cfg.handle}", falling back to auto-generated ring`);
                    } else {
                        if (faderEntry) faderEntry.userHandleEl = rotationHandleEl;
                    }
                }

                if (!rotationHandleEl) {
                    // Auto-generate rotation ring around hit label
                    rotationRingData = createRotationRing(hitRecord, cfg.hmode, cfg.rotrange);
                    if (rotationRingData && faderEntry) {
                        faderEntry.rotationRing = rotationRingData;
                    }
                }

                // Determine the element to use for rotation drag
                const rotDragTarget = rotationHandleEl || (rotationRingData ? rotationRingData.hit : null);

                if (rotDragTarget) {
                    // Set up the rotation drag handler
                    initO2PRotationDragHandler(
                        rotDragTarget,
                        hitRecord,
                        pathEl,
                        cfg,
                        // onRotate callback
                        (angleDeg) => {
                            const constrained = constrainRotation(angleDeg, cfg.hmode, cfg.rotrange);
                            const normP = normalizeRotation(constrained, cfg.hmode, cfg.rotrange);

                            if (faderEntry) {
                                faderEntry.curAngle = constrained;
                                faderEntry.curP = normP;
                            }

                            // Update visual indicator
                            if (rotationRingData) {
                                updateRotationIndicator(rotationRingData, constrained);
                            }
                            if (rotationHandleEl) {
                                updateO2PUserHandle(faderEntry, constrained);
                            }

                            // Publish to control plane
                            const publishData = {
                                t: faderEntry ? faderEntry.curT : (cfg._currentT ?? 0),
                                p: normP
                            };

                            publish("o2p", cfg.uid, publishData);

                            // OSC emit with rotation
                            if (cfg.oscCfg?.enabled) {
                                emitO2POsc({
                                    cfg,
                                    uid: cfg.uid,
                                    path: pathEl,
                                    point: cfg._currentPoint,
                                    pathT: cfg._currentT || 0,
                                    rotationP: normP
                                });
                            }
                        }
                    );

                }
            }

            // Mark as ready (no animation running, but interactive)
            cfg._touchModeActive = true;

            // Send initial value if not already set by late-join sync or default
            // callback; a live re-run starts the fader where the motion was
            if (resume) {
                cfg._initialValueSent = true;
                updatePosition(resume.t, resume.t);
            } else if (!cfg._initialValueSent) {
                const initialT = cfg.startPos ?? 0;
                cfg._initialValueSent = true;
                updatePosition(initialT, initialT);
            }

            // Touch mode does NOT auto-start animation - it's purely user-driven
            // Return here to skip ALL further processing including scheduleCueStart
            return;
        }

        // -----------------------------------------------------------
        // NON-TOUCH MODES: Register with visibility / pause / resume system
        // -----------------------------------------------------------
        registerAnimation(el, "o2p", cfg, () => {
            if (cfg.trig === "edge" && !cfg._edgeTriggered) {
                return;
            }

            if (cfg._armed && cfg._startBlocked) {
                return;
            }

            applyPrestateOnStart(el, cfg);
            rawStart();
        });

        if (cfg.scrub) installScrub(el, cfg);

        // Create standard hit label for non-touch modes (unless hit:0)
        if (cfg.hit !== false) createHitLabel(el, "o2p", cfg.uid, {
            anchorMode: "object",
            color: "purple",
            sizeMode: "follow",
            oscToggle: cfg.oscToggle,
            toggleStates: cfg.toggleStates,
            toggleColors: cfg.toggleColors,
            onToggle: cfg.onToggle
        });

        // -----------------------------------------------------------
        // AUTO-START (tdelay)
        // -----------------------------------------------------------
        if (shouldStartNow) {

            autoStartAnimation(cfg.uid, () => scheduleCueStart(cfg, el, () => {

                if (cfg._armed && cfg._startBlocked) {
                    applyPrestateOnStart(el, cfg);
                    return;
                }

                applyPrestateOnStart(el, cfg);
                rawStart();

            }, cfg.uid), fromCueTrigger);
        }

    } catch (err) {
        console.warn("[o2p] handleO2PCue failed:", err);
    }
}


// ============================================================================
// GENERIC TARGET BUTTON HANDLER
// When a click-type o2p element with `target` parameter is clicked,
// move the target fader to `targetT` value (or toggle between values).
//
// DSL usage:
//   o2p(path:..., trig:click, target:faderId, targetT:0.8, group:groupName)
//   o2p(path:..., trig:click, target:faderId, toggle:0,0.8, group:groupName)
// ============================================================================

const _toggleStates = {};  // Track toggle state per uid

document.addEventListener("oscilla-hit", (e) => {
    const uid = e.detail?.uid;
    if (!uid) return;

    // Find the element and its config
    const el = document.querySelector(`[id*="uid:${uid}"], [data-oscilla*="uid:${uid}"]`) ||
               document.getElementById(uid);
    if (!el) return;

    const cfg = el._oscillaCfg;
    if (!cfg || !cfg.target || !cfg.group) return;

    // Find the group
    const group = window._o2pTouchGroups?.[cfg.group];
    if (!group) {
        console.warn(`[o2p] Target button: group "${cfg.group}" not found`);
        return;
    }

    // Check if target fader exists
    if (!group.faders[cfg.target]) {
        console.warn(`[o2p] Target button: fader "${cfg.target}" not found in group "${cfg.group}"`);
        return;
    }

    let targetT;

    // Toggle mode: alternate between two values
    if (cfg.toggle && Array.isArray(cfg.toggle) && cfg.toggle.length >= 2) {
        const stateKey = `${cfg.group}:${cfg.target}`;
        _toggleStates[stateKey] = !_toggleStates[stateKey];
        targetT = _toggleStates[stateKey] ? cfg.toggle[1] : cfg.toggle[0];
    }
    // Single targetT mode
    else if (cfg.targetT != null) {
        targetT = cfg.targetT;
    }
    else {
        return;  // No target value specified
    }

    // Move the target fader visually
    group.setPosition(cfg.target, targetT, undefined);
}, true);